import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveRangeMidpoint } from '@/utils/nutritionGoals';
import {
  changeMealPlanDates,
  assignGenericMealPlan,
  createManualMealPlanDraft,
  createGenericMealPlan,
  deleteDraft,
  deleteGenericMealPlan,
  deleteMealPlan,
  deleteMealPlanDay,
  getClientById,
  getClients,
  getGenericClinicalTags,
  getMealPrograms,
  getMealPlanDraftByIdForAIDisplay,
  getMealPlanExportForPdf,
  getMealPlanForDisplay,
  getGenericMealPlanForDisplay,
  getMealPlansAll,
  listGenericMealPlans,
  reorderManualMealSlots,
  reorderMealPlanDays,
  reorderMealPlanMeals,
  swapMealPlanMeals,
  updateMealPlanDayName,
  updateMealPlanNotes,
} from '@/lib/apiService';
import { copyManualMealPlanDay, finalizeManualMealPlan, importMealPlanTemplate } from '@/lib/api/mealPlans';
import type { GenericMealPlanSummary, UpdateMealPlanNotesRequest } from '@/lib/api/mealPlans';
import type { FinalizedMealPlanData, MealPlanAllPlanItem, MealPlanExportForPdfData } from '@/types/meal-plan';
import type { Client } from '@/types/client';
import { useDebounce } from '@/hooks/useDebounce';
import { normalizeMealTypeForSelect } from '@/utils/meal-type-utils';
import { toGenericMealPlanSlots } from '@/utils/generic-meal-plan';
import {
  ClientDetailsV2AddMealPlanDialog,
  type GenericPlanCalorieTargetForm,
} from '@/clients/details/v2/components/ClientDetailsV2AddMealPlanDialog';
import type { AddDayPayload } from '@/clients/details/v2/components/ClientDetailsV2AddDayDialog';
import { ClientDetailsV2MealPlanListItem } from '@/clients/details/v2/components/ClientDetailsV2MealPlanListItem';
import { ClientDetailsV2MealPlanBoardView } from '@/clients/details/v2/components/ClientDetailsV2MealPlanBoardView';
import { MealPlanClientFeedbackPanel } from '@/clients/details/v2/components/MealPlanClientFeedbackPanel';
import SearchableClientDropdown from '@/components/shared/SearchableClientDropdown';
import InlineFeedback from '@/components/shared/InlineFeedback';
import { extractErrorMessage, showErrorToast, showNotesSavedToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import {
  extractMealPlanDateConflicts,
  formatMealPlanDateConflictMessage,
  type MealPlanDateConflictItem,
} from '@/utils/meal-plan-date-conflicts';
import { buildMealPlanRestrictionWarning } from '@/utils/meal-plan-allergen-warnings';
import { useAuth } from '@/hooks/useAuth';
import {
  downloadGenericMealPlanPdfFromExportApi,
  downloadMealPlanPdfFromExportApi,
} from '@/utils/meal-plan-ingredients-pdf';
import { unwrapMealPlanExportData } from '@/utils/meal-plan-export';
import { formatDelimitedDisplayLabel } from '@/utils/display-labels';
import SlashSearchInput from '@/components/shared/SlashSearchInput';
import PaginationControls from '@/components/shared/PaginationControls';
import { AI_MEAL_PLAN_MAX_DAYS, MEAL_PLAN_MAX_DAYS } from '@/components/meal-planning/MealPlanScheduleFields';
import { trackMealPlanDayDeleteClicked } from '@/lib/analytics/trackers/mealPlanActions';
import {
  trackAddMealPlanClicked,
  trackAIMealPlanSetupStarted,
  trackManualMealPlanSetupStarted,
  trackMealPlanTypeSelected,
} from '@/lib/analytics/trackers/mealPlanTab';

interface ClientDetailsV2MealPlanTabProps {
  clientId: string;
  client?: Client | null;
  onMealPlanBack?: () => void;
  genericLibraryOnly?: boolean;
}

type MealPlanCategory = 'manual' | 'auto' | 'ai' | 'generic';

const DEFAULT_GENERIC_CALORIE_TARGET: GenericPlanCalorieTargetForm = {
  mode: 'direct',
  dailyCalories: '1900',
  ageYears: '',
  gender: '',
  weightKg: '',
  heightCm: '',
  activityLevel: 'moderately_active',
  weightGoal: 'maintain',
};
type ClientTimelineCategory = 'all' | 'manual' | 'generic' | 'ai';

type MealPlanListItem = {
  id: string;
  category: MealPlanCategory;
  title: string;
  clientName?: string;
  durationLabel: string;
  tag: string;
  tags?: string[];
  calories: number | null;
  carbs: number | null;
  protein: number | null;
  fat: number | null;
  assignedClients: number | null;
  status: 'Finalized' | 'Draft';
  rawStatus: string;
  planType?: string;
  generationType?: string;
  generationSource?: string;
  creationMethod?: string;
  creationType?: string;
  type?: string;
  source?: string;
  sourceClientId?: string;
  startDate?: string;
  endDate?: string;
  timelineStatus?: 'past' | 'current' | 'future';
  dateRangeLabel?: string;
};

function isGenericLibraryMealPlan(
  meal: Pick<MealPlanListItem, 'category' | 'rawStatus' | 'creationMethod'> | null | undefined
): boolean {
  return (
    meal?.category === 'generic' &&
    meal.rawStatus === 'generic' &&
    String(meal.creationMethod ?? '').trim().toLowerCase() === 'generic'
  );
}

function isAssignedGenericMealPlan(
  meal: Pick<MealPlanListItem, 'category' | 'rawStatus' | 'creationMethod'> | null | undefined
): boolean {
  const creationMethod = String(meal?.creationMethod ?? '').trim().toLowerCase();
  return creationMethod === 'generic_assigned' || (meal?.category === 'generic' && meal.rawStatus !== 'generic');
}

type MealPlanFetchResult<T> = {
  plans: T[];
  totalCount: number;
};

/** Tabs on `/meal-planning` (and client meal-plan views): each filters the plan list by inferred plan category (`toCategory`). */
const MEAL_PLAN_LIST_PAGE_SIZE = 10;

const categoryTabs: Array<{ key: MealPlanCategory; label: string }> = [
  { key: 'manual', label: 'Manual' },
  { key: 'generic', label: 'Cohort Meal' },
  { key: 'ai', label: 'AI Meal Plan' },
];

const clientTimelineTabs: Array<{ key: ClientTimelineCategory; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'manual', label: 'Manual' },
  { key: 'generic', label: 'Cohort Meal' },
  { key: 'ai', label: 'AI Meal Plan' },
];

const defaultByCategory: Record<MealPlanCategory, { tag: string }> = {
  manual: { tag: 'High Protein' },
  auto: { tag: 'Auto Generated' },
  ai: { tag: 'AI Optimized' },
  generic: { tag: 'Cohort Meal' },
};
const recognizedFinalizedMealTypes = new Set(['breakfast', 'lunch', 'dinner', 'snack', 'other']);

function toCategory(plan: MealPlanAllPlanItem): MealPlanCategory {
  const generationType = String(plan.generationType ?? '').toLowerCase();
  const generationSource = String((plan as any).generationSource ?? '').toLowerCase();
  const source = String((plan as any).source ?? '').toLowerCase();
  const creationMethod = String((plan as any).creationMethod ?? '').toLowerCase();
  const aiProviderRaw = (plan as any).aiProvider;
  const hasAiProvider = aiProviderRaw != null && String(aiProviderRaw).trim() !== '';

  const structured = [
    generationType,
    String(plan.planType ?? '').toLowerCase(),
    creationMethod,
    String((plan as any).creationType ?? '').toLowerCase(),
    generationSource,
    source,
    String((plan as any).type ?? '').toLowerCase(),
  ].join(' ');

  if (generationType.includes('ai') || generationSource === 'ai' || hasAiProvider) return 'ai';
  if (structured.includes('ai')) return 'ai';
  if (structured.includes('automated') || structured.includes('auto')) return 'auto';
  if (
    generationType === 'generic'
    || generationSource === 'generic'
    || generationSource === 'generic_assigned'
    || creationMethod === 'generic_assigned'
    || (source === 'generic' && creationMethod !== 'generic')
  ) {
    return 'generic';
  }
  if (generationType === 'manual' || generationSource === 'manual' || String((plan as any).type ?? '').toLowerCase() === 'manual') {
    return 'manual';
  }
  return 'manual';
}

function resolveNumber(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function resolveProvidedNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function resolvePositiveProvidedNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function roundProvidedNumber(value: number | null): number | null {
  return value === null ? null : Number(value.toFixed(2));
}

function getPaginationNumber(
  pagination: unknown,
  keys: string[],
): number | null {
  if (!pagination || typeof pagination !== 'object') return null;
  const record = pagination as Record<string, unknown>;

  for (const key of keys) {
    const raw = record[key];
    const value = typeof raw === 'string' ? Number(raw) : raw;
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.floor(value));
    }
  }

  return null;
}

function getTotalPagesFromPagination(
  pagination: unknown,
  totalCount: number,
  pageSize: number,
): number {
  const explicitTotalPages = getPaginationNumber(pagination, ['totalPages', 'pages', 'pageCount', 'total_pages']);
  if (explicitTotalPages && explicitTotalPages > 0) return explicitTotalPages;
  if (totalCount > 0 && pageSize > 0) return Math.max(1, Math.ceil(totalCount / pageSize));
  return 1;
}

function parseDateOnlyMillis(value?: string): number | null {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}

const DATE_ONLY_DAY_MS = 1000 * 60 * 60 * 24;

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayDateInputValue(): string {
  return formatDateInputValue(new Date());
}

function addDaysToDateOnly(value: string, days: number): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() + days);
  return formatDateInputValue(date);
}

function getInclusiveDateRangeDays(startDate: string, endDate: string): number | null {
  const start = parseDateOnlyMillis(startDate);
  const end = parseDateOnlyMillis(endDate);
  if (start == null || end == null) return null;
  return Math.floor((end - start) / DATE_ONLY_DAY_MS) + 1;
}

function clampAssignmentEndDate(startDate: string, endDate: string): string {
  if (!startDate) return endDate;
  const maxEndDate = addDaysToDateOnly(startDate, MEAL_PLAN_MAX_DAYS - 1);
  if (!endDate) return startDate;
  const end = parseDateOnlyMillis(endDate);
  const start = parseDateOnlyMillis(startDate);
  const maxEnd = parseDateOnlyMillis(maxEndDate);
  if (end == null || start == null || maxEnd == null) return endDate;
  if (end < start) return startDate;
  if (end > maxEnd) return maxEndDate;
  return endDate;
}

function formatDateRangeLabel(startDate?: string, endDate?: string): string {
  if (!startDate && !endDate) return '';
  const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const format = (value?: string) => {
    const time = parseDateOnlyMillis(value);
    return time == null ? '' : formatter.format(new Date(time));
  };
  const start = format(startDate);
  const end = format(endDate);
  if (start && end && start !== end) return `${start} - ${end}`;
  return start || end;
}

function classifyTimelineStatus(startDate?: string, endDate?: string): 'past' | 'current' | 'future' {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTime = today.getTime();
  const start = parseDateOnlyMillis(startDate);
  const end = parseDateOnlyMillis(endDate) ?? start;
  if (start != null && start > todayTime) return 'future';
  if (end != null && end < todayTime) return 'past';
  return 'current';
}

function formatApiTag(value: unknown): string {
  return String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const response = (error as { response?: { status?: unknown; data?: { message?: unknown; error?: unknown } } }).response;
  if (Number(response?.status) === 404) return true;

  const message = [
    (error as { message?: unknown }).message,
    response?.data?.message,
    response?.data?.error,
  ]
    .map((value) => String(value ?? '').toLowerCase())
    .join(' ');

  return message.includes('not found');
}

function toMealPlanItem(plan: MealPlanAllPlanItem): MealPlanListItem {
  const category = toCategory(plan);
  const defaults = defaultByCategory[category];
  const targetDaily = (plan as any).targetDaily && typeof (plan as any).targetDaily === 'object'
    ? (plan as any).targetDaily as Record<string, unknown>
    : {};
  const overallNutrition = (plan as any).overallNutrition && typeof (plan as any).overallNutrition === 'object'
    ? (plan as any).overallNutrition as Record<string, unknown>
    : {};

  const calories = resolveProvidedNumber(
    overallNutrition.calories,
    overallNutrition.totalCalories
  ) ?? resolvePositiveProvidedNumber(
    targetDaily.calories,
    (plan as any).targetCalories,
    (plan as any).targets?.calories
  );

  const carbs = resolveProvidedNumber(
    overallNutrition.carbs,
    overallNutrition.carbohydrate,
    overallNutrition.totalCarbs
  ) ?? resolveRangeMidpoint(
    targetDaily.carbsMin,
    targetDaily.carbsMax
  ) ?? resolvePositiveProvidedNumber(targetDaily.carbs) ?? resolveProvidedNumber(
    (plan as any).macroSplit?.carbs,
    (plan as any).macroDistribution?.carbs,
    (plan as any).carbsPercentage,
    (plan as any).carbPercentage,
    (plan as any).carbsPercent,
    (plan as any).targets?.carbsPercent
  );

  const protein = resolveProvidedNumber(
    overallNutrition.protein,
    overallNutrition.totalProtein
  ) ?? resolveRangeMidpoint(
    targetDaily.proteinMin,
    targetDaily.proteinMax
  ) ?? resolvePositiveProvidedNumber(targetDaily.protein) ?? resolveProvidedNumber(
    (plan as any).macroSplit?.protein,
    (plan as any).macroDistribution?.protein,
    (plan as any).proteinPercentage,
    (plan as any).proteinPercent,
    (plan as any).targets?.proteinPercent
  );

  const fat = resolveProvidedNumber(
    overallNutrition.fat,
    overallNutrition.fats,
    overallNutrition.totalFat,
    overallNutrition.total_fat
  ) ?? resolveRangeMidpoint(
    targetDaily.fatMin,
    targetDaily.fatMax
  ) ?? resolvePositiveProvidedNumber(targetDaily.fat) ?? resolveProvidedNumber(
    (plan as any).macroSplit?.fat,
    (plan as any).macroDistribution?.fat,
    (plan as any).fatPercentage,
    (plan as any).fatPercent,
    (plan as any).targets?.fatPercent
  );

  const assignedClients =
    resolveProvidedNumber(
      (plan as any).assignedClients,
      (plan as any).assignedClientsCount,
      (plan as any).clientsAssigned,
      (plan as any).clientCount,
      (plan as any).summary?.assignedClients
    );

  const durationDays = resolveProvidedNumber(
    plan.durationDays,
    (plan as any).days,
    (plan as any).duration,
    (plan as any).dayCount,
    (plan as any).daysCount,
    (plan as any).totalDays,
  );
  const planTags = Array.isArray(plan.tags)
    ? plan.tags.map(formatApiTag).filter(Boolean)
    : [];
  const firstTag = planTags[0] || defaults.tag;
  const rawStatus = String(plan.status ?? (plan as any).planStatus ?? '').toLowerCase();
  const clientName = String(
    (plan as any).client?.name
      ?? (plan as any).client?.fullName
      ?? '',
  ).trim();

  return {
    id: plan.id,
    category,
    title: plan.name?.trim() || `${category.toUpperCase()} Meal Plan`,
    clientName: clientName || undefined,
    durationLabel: durationDays !== null ? `${Math.max(1, Math.round(durationDays))}-Day Plan` : '',
    tag: firstTag,
    tags: planTags,
    calories: roundProvidedNumber(calories),
    carbs: roundProvidedNumber(carbs),
    protein: roundProvidedNumber(protein),
    fat: roundProvidedNumber(fat),
    assignedClients: assignedClients === null ? null : Math.max(0, Math.round(assignedClients)),
    status: rawStatus === 'draft' ? 'Draft' : 'Finalized',
    rawStatus,
    planType: plan.planType,
    generationType: plan.generationType,
    generationSource: (plan as any).generationSource,
    creationMethod: (plan as any).creationMethod,
    creationType: (plan as any).creationType,
    type: (plan as any).type,
    source: (plan as any).source,
    sourceClientId: plan.clientId ?? plan.client?.id,
    startDate: String((plan as any).startDate ?? '').slice(0, 10) || undefined,
    endDate: String((plan as any).endDate ?? '').slice(0, 10) || undefined,
  };
}

function toGenericMealPlanItem(plan: GenericMealPlanSummary): MealPlanListItem {
  const mealSlotCount = resolveNumber(plan.mealsCount, (plan as any).meals_count)
    ?? (Array.isArray(plan.mealSlots) ? plan.mealSlots.length : 0);
  const durationDays = resolveProvidedNumber(plan.durationDays, (plan as any).days, (plan as any).dayCount);
  const overallNutrition = (plan as any).overallNutrition && typeof (plan as any).overallNutrition === 'object'
    ? (plan as any).overallNutrition as Record<string, unknown>
    : {};
  const calories = resolveProvidedNumber(
    overallNutrition.calories,
    overallNutrition.totalCalories,
    (plan as any).totals?.calories,
    (plan as any).totals?.totalCalories,
    plan.totalCalories,
    plan.calories,
  );
  const carbs = resolveProvidedNumber(
    overallNutrition.carbs,
    overallNutrition.carbohydrate,
    overallNutrition.totalCarbs,
    (plan as any).totals?.carbs,
    (plan as any).totals?.totalCarbs,
    (plan as any).macroSplit?.carbs,
    (plan as any).macroDistribution?.carbs,
    (plan as any).carbsPercentage,
    (plan as any).carbPercentage,
    (plan as any).carbsPercent,
    (plan as any).targets?.carbsPercent,
  );
  const protein = resolveProvidedNumber(
    overallNutrition.protein,
    overallNutrition.totalProtein,
    (plan as any).totals?.protein,
    (plan as any).totals?.totalProtein,
    (plan as any).macroSplit?.protein,
    (plan as any).macroDistribution?.protein,
    (plan as any).proteinPercentage,
    (plan as any).proteinPercent,
    (plan as any).targets?.proteinPercent,
  );
  const fat = resolveProvidedNumber(
    overallNutrition.fat,
    overallNutrition.fats,
    overallNutrition.totalFat,
    overallNutrition.total_fat,
    (plan as any).totals?.fat,
    (plan as any).totals?.totalFat,
    (plan as any).macroSplit?.fat,
    (plan as any).macroDistribution?.fat,
    (plan as any).fatPercentage,
    (plan as any).fatPercent,
    (plan as any).targets?.fatPercent,
  );
  const assignedClients = resolveProvidedNumber(
    (plan as any).assignedClients,
    (plan as any).assignedClientsCount,
    (plan as any).clientsAssigned,
    (plan as any).clientCount,
    (plan as any).summary?.assignedClients,
  );
  const clientName = String(
    (plan as any).client?.name
      ?? (plan as any).client?.fullName
      ?? (plan as any).clientName
      ?? '',
  ).trim();
  const availableTagLabels = Array.isArray(plan.availableClinicalTags)
    ? new Map(
        plan.availableClinicalTags.map((tag) => [
          String(tag.id ?? tag.value ?? tag.name ?? '').trim(),
          String(tag.label ?? tag.name ?? tag.value ?? tag.id ?? '').trim(),
        ])
      )
    : new Map<string, string>();
  const clinicalTagLabels = Array.isArray(plan.clinicalTags)
    ? plan.clinicalTags
        .map((tag) => {
          const normalizedTag = String(tag).trim();
          return availableTagLabels.get(normalizedTag) || formatApiTag(normalizedTag);
        })
        .filter(Boolean)
    : [];
  const firstTag = clinicalTagLabels[0] || defaultByCategory.generic.tag;

  return {
    id: String(plan.id),
    category: 'generic',
    title: plan.name?.trim() || 'Cohort Meal Plan',
    durationLabel: mealSlotCount > 0
      ? `${mealSlotCount} Meal Slots`
      : durationDays !== null
        ? `${Math.max(1, Math.round(durationDays))}-Day Plan`
        : '',
    clientName: clientName || undefined,
    tag: firstTag,
    tags: clinicalTagLabels,
    calories: roundProvidedNumber(calories),
    carbs: roundProvidedNumber(carbs),
    protein: roundProvidedNumber(protein),
    fat: roundProvidedNumber(fat),
    assignedClients: assignedClients === null ? null : Math.max(0, Math.round(assignedClients)),
    status: 'Draft',
    rawStatus: 'generic',
    planType: 'generic',
    generationType: 'generic',
    creationMethod: 'generic',
    type: 'generic',
    sourceClientId: plan.clientId,
    startDate: String((plan as any).startDate ?? '').slice(0, 10) || undefined,
    endDate: String((plan as any).endDate ?? '').slice(0, 10) || undefined,
  };
}

export const ClientDetailsV2MealPlanTab: React.FC<ClientDetailsV2MealPlanTabProps> = ({
  clientId,
  client,
  onMealPlanBack,
  genericLibraryOnly = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeStateMealPlan = React.useMemo(() => {
    const state = location.state as { mealPlanningViewMeal?: MealPlanListItem } | null;
    return state?.mealPlanningViewMeal?.id ? state.mealPlanningViewMeal : null;
  }, [location.state]);
  const initialRouteMealPlanRef = React.useRef<MealPlanListItem | null>(routeStateMealPlan);
  const enableMealPlanPdfExport = true;
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = React.useState<MealPlanCategory>(() => {
    if (genericLibraryOnly) return 'generic';
    const queryCategory = new URLSearchParams(location.search).get('category')?.trim().toLowerCase();
    if (queryCategory === 'ai' || queryCategory === 'auto' || queryCategory === 'manual' || queryCategory === 'generic') {
      return queryCategory as MealPlanCategory;
    }
    return 'manual';
  });
  const [clientTimelineCategory, setClientTimelineCategory] = React.useState<ClientTimelineCategory>(() => {
    const queryCategory = new URLSearchParams(location.search).get('category')?.trim().toLowerCase();
    if (queryCategory === 'ai') return 'ai';
    if (queryCategory === 'generic') return 'generic';
    if (queryCategory === 'manual' || queryCategory === 'auto') return 'manual';
    return 'all';
  });
  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedMealPlanSearch = useDebounce(searchQuery, 300);
  const [mealPlans, setMealPlans] = React.useState<MealPlanListItem[]>([]);
  const [totalByCategory, setTotalByCategory] = React.useState<Record<MealPlanCategory, number>>({
    manual: 0,
    auto: 0,
    ai: 0,
    generic: 0,
  });
  const [mealPlansRefreshKey, setMealPlansRefreshKey] = React.useState(0);
  const [mealPlanListPage, setMealPlanListPage] = React.useState(1);
  const [mealPlanListPagination, setMealPlanListPagination] = React.useState({
    totalPages: 1,
    totalCount: 0,
  });
  const [loading, setLoading] = React.useState(false);
  const [mealPlansLoadError, setMealPlansLoadError] = React.useState('');
  const [activeMealPlan, setActiveMealPlan] = React.useState<MealPlanListItem | null>(() => routeStateMealPlan);
  const [activeMealPlanData, setActiveMealPlanData] = React.useState<FinalizedMealPlanData | null>(null);
  const [mealPlanExportData, setMealPlanExportData] = React.useState<MealPlanExportForPdfData | null>(null);
  const [isLoadingMealPlanDetails, setIsLoadingMealPlanDetails] = React.useState(Boolean(routeStateMealPlan));
  const [isSavingDayName, setIsSavingDayName] = React.useState(false);
  const [isSavingMealPlanNotes, setIsSavingMealPlanNotes] = React.useState(false);
  const [isDeletingDay, setIsDeletingDay] = React.useState(false);
  const [isCopyingDay, setIsCopyingDay] = React.useState(false);
  const [isReorderingDays, setIsReorderingDays] = React.useState(false);
  const [isReorderingMeals, setIsReorderingMeals] = React.useState(false);
  const [deletingMealPlanId, setDeletingMealPlanId] = React.useState<string | null>(null);
  const [downloadingMealPlanId, setDownloadingMealPlanId] = React.useState<string | null>(null);
  const [isExportingActiveMealPlanPdf, setIsExportingActiveMealPlanPdf] = React.useState(false);
  const [isFinalizingActiveMealPlan, setIsFinalizingActiveMealPlan] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [pendingDeleteMealPlan, setPendingDeleteMealPlan] = React.useState<MealPlanListItem | null>(null);
  const [assignGenericPlan, setAssignGenericPlan] = React.useState<MealPlanListItem | null>(null);
  const [assignClientId, setAssignClientId] = React.useState('');
  const [assignStartDate, setAssignStartDate] = React.useState('');
  const [assignEndDate, setAssignEndDate] = React.useState('');
  const [isAssigningGenericPlan, setIsAssigningGenericPlan] = React.useState(false);
  const [assignDateConflicts, setAssignDateConflicts] = React.useState<MealPlanDateConflictItem[]>([]);
  const [assignGenericFieldErrors, setAssignGenericFieldErrors] = React.useState<{
    client?: string;
    startDate?: string;
    endDate?: string;
  }>({});
  const [assignGenericFormFeedback, setAssignGenericFormFeedback] = React.useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);
  const [isCreatingGenericMealPlan, setIsCreatingGenericMealPlan] = React.useState(false);
  const [mealPlanDetailsError, setMealPlanDetailsError] = React.useState('');
  const [isAddMealModalOpen, setIsAddMealModalOpen] = React.useState(false);
  const [selectedPlanType, setSelectedPlanType] = React.useState<MealPlanCategory>('manual');
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [selectedClientQuery, setSelectedClientQuery] = React.useState('');
  const [isClientDropdownOpen, setIsClientDropdownOpen] = React.useState(false);
  const [clientOptions, setClientOptions] = React.useState<Client[]>([]);
  const [isClientsLoading, setIsClientsLoading] = React.useState(false);
  const [newMealName, setNewMealName] = React.useState('');
  const [newMealDays, setNewMealDays] = React.useState<number>(7);
  const [genericClinicalTags, setGenericClinicalTags] = React.useState<Array<{ value: string; label: string }>>([]);
  const [selectedGenericClinicalTags, setSelectedGenericClinicalTags] = React.useState<string[]>([]);
  const [genericCalorieTarget, setGenericCalorieTarget] = React.useState<GenericPlanCalorieTargetForm>(
    DEFAULT_GENERIC_CALORIE_TARGET,
  );
  const [isGenericClinicalTagsLoading, setIsGenericClinicalTagsLoading] = React.useState(false);
  const [hasLoadedGenericClinicalTags, setHasLoadedGenericClinicalTags] = React.useState(false);
  const [addMealPlanFieldErrors, setAddMealPlanFieldErrors] = React.useState<{
    mealName?: string;
    client?: string;
    clinicalTags?: string;
    calorieTarget?: string;
    ageYears?: string;
    gender?: string;
    weightKg?: string;
    heightCm?: string;
    activityLevel?: string;
  }>({});
  const [addMealPlanFormFeedback, setAddMealPlanFormFeedback] = React.useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);
  const isMealPlanningRoute = location.pathname.startsWith('/meal-planning');
  const showClientSelection = !genericLibraryOnly && !clientId.trim() && selectedPlanType === 'generic';
  const showClientChronology = Boolean(clientId.trim()) && !genericLibraryOnly;
  const shouldFetchGenericMealPlans = genericLibraryOnly;
  const usePaginatedMealPlanList = (isMealPlanningRoute || genericLibraryOnly) && !showClientChronology;
  const getMealPlanEntryProperties = React.useCallback(() => {
    const entrySource = genericLibraryOnly
      ? 'generic_library'
      : showClientChronology
        ? 'client_details'
        : isMealPlanningRoute
          ? 'meal_planning_navigation'
          : 'meal_plan_tab';

    return {
      active_meal_plan_category: activeCategory,
      has_context_client: Boolean(clientId.trim()),
      meal_plan_entry_source: entrySource,
      meal_plan_surface: 'meal_plan_list',
      product_area: 'meal_plans',
    };
  }, [activeCategory, clientId, genericLibraryOnly, isMealPlanningRoute, showClientChronology]);

  React.useEffect(() => {
    setMealPlanListPage(1);
  }, [activeCategory, clientId, debouncedMealPlanSearch, mealPlansRefreshKey, shouldFetchGenericMealPlans]);
  const debouncedClientSearch = useDebounce(selectedClientQuery, 250);
  const assignDateToday = React.useMemo(() => getTodayDateInputValue(), [assignGenericPlan?.id]);
  const assignMaxEndDate = React.useMemo(
    () => addDaysToDateOnly(assignStartDate || assignDateToday, MEAL_PLAN_MAX_DAYS - 1),
    [assignDateToday, assignStartDate]
  );

  React.useEffect(() => {
    if (!genericLibraryOnly || !isAddMealModalOpen) return;
    setSelectedPlanType('generic');
    setNewMealName('');
    setSelectedGenericClinicalTags([]);
    setGenericCalorieTarget(DEFAULT_GENERIC_CALORIE_TARGET);
  }, [genericLibraryOnly, isAddMealModalOpen]);

  const normalizeSwapMealName = React.useCallback((mealKey: string): string => {
    const raw = String(mealKey ?? '').trim();
    if (!raw) return '';
    const withoutDuplicateSuffix = raw.replace(/_\d+$/, '');
    const normalizedType = normalizeMealTypeForSelect(withoutDuplicateSuffix);
    if (normalizedType !== 'other') {
      return normalizedType;
    }
    return withoutDuplicateSuffix.toLowerCase().replace(/\s+/g, '-');
  }, []);

  const getMealPlanDetailsForView = React.useCallback(
    async (mealPlanId: string, isDraft: boolean) => {
      if (isDraft) {
        return getMealPlanDraftByIdForAIDisplay(mealPlanId);
      }
      return getMealPlanForDisplay(mealPlanId);
    },
    []
  );

  const resolveDayCountFromPlanDetails = React.useCallback((details: FinalizedMealPlanData | null | undefined): number | null => {
    if (!details) return null;
    const durationDays = Number((details as { durationDays?: unknown }).durationDays);
    if (Number.isFinite(durationDays) && durationDays > 0) {
      return Math.max(1, Math.round(durationDays));
    }
    const dayCountFromArray = Array.isArray(details.days) ? details.days.length : 0;
    if (dayCountFromArray > 0) return dayCountFromArray;
    return null;
  }, []);

  const syncMealPlanCardDuration = React.useCallback((mealPlanId: string, dayCount: number) => {
    const normalizedDayCount = Math.max(1, Math.round(dayCount));
    const nextDurationLabel = `${normalizedDayCount}-Day Plan`;

    setMealPlans((prev) => {
      let hasChanges = false;
      const next = prev.map((item) => {
        if (item.id !== mealPlanId || item.durationLabel === nextDurationLabel) return item;
        hasChanges = true;
        return {
          ...item,
          durationLabel: nextDurationLabel,
        };
      });
      return hasChanges ? next : prev;
    });

    setActiveMealPlan((prev) => {
      if (!prev || prev.id !== mealPlanId || prev.durationLabel === nextDurationLabel) return prev;
      return {
        ...prev,
        durationLabel: nextDurationLabel,
      };
    });
  }, []);

  React.useEffect(() => {
    if (!activeMealPlan?.id || !activeMealPlanData) return;
    const dayCount = resolveDayCountFromPlanDetails(activeMealPlanData);
    if (!dayCount) return;
    syncMealPlanCardDuration(activeMealPlan.id, dayCount);
  }, [activeMealPlan?.id, activeMealPlanData, resolveDayCountFromPlanDetails, syncMealPlanCardDuration]);

  const activeMealPlanId = activeMealPlan?.id || '';

  React.useEffect(() => {
    let isMounted = true;
    const normalizedClientId = clientId?.trim() || undefined;
    const search = debouncedMealPlanSearch.trim();

    if (location.pathname.startsWith('/meal-planning/client/') && activeMealPlanId) {
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchMealPlansPage = async (
      type: 'manual' | 'ai_assisted' | 'generic',
      page: number,
      pageSize: number,
      searchValue?: string,
    ) => {
      const response = await getMealPlansAll({
        clientId: normalizedClientId,
        type,
        search: searchValue || undefined,
        page,
        pageSize,
        sortBy: 'updated_at',
        sortOrder: 'desc',
      });

      const pagePlans = response.data?.plans ?? [];
      const pagination = response.data?.pagination;
      const totalCount =
        getPaginationNumber(pagination, ['totalCount', 'total', 'totalItems', 'count', 'total_count'])
        ?? pagePlans.length;

      return {
        plans: pagePlans,
        totalCount,
        totalPages: getTotalPagesFromPagination(pagination, totalCount, pageSize),
      };
    };

    const fetchGenericMealPlansPage = async (
      page: number,
      pageSize: number,
      searchValue?: string,
    ) => {
      const response = await listGenericMealPlans({
        clientId: normalizedClientId,
        search: searchValue || undefined,
        page,
        pageSize,
      });

      const pagePlans = response.data?.genericPlans ?? [];
      const pagination = (response.data as { pagination?: unknown })?.pagination;
      const totalCount =
        getPaginationNumber(pagination, ['totalCount', 'total', 'totalItems', 'count', 'total_count'])
        ?? pagePlans.length;

      return {
        plans: pagePlans,
        totalCount,
        totalPages: getTotalPagesFromPagination(pagination, totalCount, pageSize),
      };
    };

    const fetchMealPlansAllPages = async (
      type: 'manual' | 'ai_assisted' | 'generic',
      searchValue?: string
    ): Promise<MealPlanFetchResult<MealPlanAllPlanItem>> => {
      const allPlans: MealPlanAllPlanItem[] = [];
      let page = 1;
      let totalPages = 1;
      let totalCount: number | null = null;

      while (page <= totalPages) {
        const response = await getMealPlansAll({
          clientId: normalizedClientId,
          type,
          search: searchValue || undefined,
          page,
          pageSize: 100,
          sortBy: 'updated_at',
          sortOrder: 'desc',
        });

        const pagePlans = response.data?.plans ?? [];
        const pagination = response.data?.pagination;
        allPlans.push(...pagePlans);
        totalPages = getPaginationNumber(pagination, ['totalPages', 'pages', 'pageCount', 'total_pages']) ?? 1;
        totalCount ??= getPaginationNumber(pagination, ['totalCount', 'total', 'totalItems', 'count', 'total_count']);
        page += 1;
      }

      return {
        plans: allPlans,
        totalCount: totalCount ?? allPlans.length,
      };
    };

    const fetchGenericMealPlansAllPages = async (
      searchValue?: string
    ): Promise<MealPlanFetchResult<GenericMealPlanSummary>> => {
      const allPlans: GenericMealPlanSummary[] = [];
      let page = 1;
      let totalPages = 1;
      let totalCount: number | null = null;

      while (page <= totalPages) {
        const response = await listGenericMealPlans({
          clientId: normalizedClientId,
          search: searchValue || undefined,
          page,
          pageSize: 100,
        });

        const pagePlans = response.data?.genericPlans ?? [];
        const pagination = (response.data as any)?.pagination;
        allPlans.push(...pagePlans);
        totalPages = getPaginationNumber(pagination, ['totalPages', 'pages', 'pageCount', 'total_pages']) ?? 1;
        totalCount ??= getPaginationNumber(pagination, ['totalCount', 'total', 'totalItems', 'count', 'total_count']);
        page += 1;
      }

      return {
        plans: allPlans,
        totalCount: totalCount ?? allPlans.length,
      };
    };

    async function fetchMealPlans() {
      setLoading(true);
      setMealPlansLoadError('');

      try {
        let counts: Record<MealPlanCategory, number> = {
          manual: 0,
          auto: 0,
          ai: 0,
          generic: 0,
        };
        let nextMealPlans: MealPlanListItem[] = [];

        if (usePaginatedMealPlanList) {
          if (shouldFetchGenericMealPlans) {
            const [countResult, pageResult] = await Promise.all([
              fetchGenericMealPlansPage(1, 1, search),
              fetchGenericMealPlansPage(mealPlanListPage, MEAL_PLAN_LIST_PAGE_SIZE, search),
            ]);
            counts = {
              manual: 0,
              auto: 0,
              ai: 0,
              generic: countResult.totalCount,
            };
            nextMealPlans = pageResult.plans.map(toGenericMealPlanItem);
            if (!isMounted) return;
            setMealPlanListPagination({
              totalPages: pageResult.totalPages,
              totalCount: countResult.totalCount,
            });
          } else {
            const activeApiType =
              activeCategory === 'ai'
                ? 'ai_assisted'
                : activeCategory === 'generic'
                  ? 'generic'
                  : 'manual';
            const [manualCountResult, genericCountResult, aiCountResult, pageResult] = await Promise.all([
              fetchMealPlansPage('manual', 1, 1, search),
              fetchMealPlansPage('generic', 1, 1, search),
              fetchMealPlansPage('ai_assisted', 1, 1, search),
              fetchMealPlansPage(activeApiType, mealPlanListPage, MEAL_PLAN_LIST_PAGE_SIZE, search),
            ]);
            counts = {
              manual: manualCountResult.totalCount,
              auto: 0,
              ai: aiCountResult.totalCount,
              generic: genericCountResult.totalCount,
            };
            nextMealPlans = pageResult.plans.map(toMealPlanItem);
            if (!isMounted) return;
            setMealPlanListPagination({
              totalPages: pageResult.totalPages,
              totalCount: pageResult.totalCount,
            });
          }
        } else if (showClientChronology) {
          const [manualResult, genericResult, aiResult] = await Promise.all([
            fetchMealPlansAllPages('manual', search),
            fetchMealPlansAllPages('generic', search),
            fetchMealPlansAllPages('ai_assisted', search),
          ]);
          counts = {
            manual: manualResult.totalCount,
            auto: 0,
            ai: aiResult.totalCount,
            generic: genericResult.totalCount,
          };
          nextMealPlans = [...manualResult.plans, ...genericResult.plans, ...aiResult.plans].map(toMealPlanItem);
        } else if (shouldFetchGenericMealPlans) {
          const genericResult = await fetchGenericMealPlansAllPages(search);
          counts = {
            manual: 0,
            auto: 0,
            ai: 0,
            generic: genericResult.totalCount,
          };
          nextMealPlans = genericResult.plans.map(toGenericMealPlanItem);
        } else {
          const [manualResult, aiResult] = await Promise.all([
            fetchMealPlansAllPages('manual', search),
            fetchMealPlansAllPages('ai_assisted', search),
          ]);
          counts = {
            manual: manualResult.totalCount,
            auto: 0,
            ai: aiResult.totalCount,
            generic: 0,
          };
          nextMealPlans = [...manualResult.plans, ...aiResult.plans].map(toMealPlanItem);
        }

        if (!isMounted) return;

        setTotalByCategory(counts);
        setMealPlans(nextMealPlans);
      } catch (error) {
        console.error('Failed to fetch meal plans for details v2 meal tab:', error);
        if (isMounted) {
          setMealPlans([]);
          setMealPlansLoadError(extractErrorMessage(error));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchMealPlans();

    return () => {
      isMounted = false;
    };
  }, [
    activeCategory,
    activeMealPlanId,
    clientId,
    debouncedMealPlanSearch,
    genericLibraryOnly,
    location.pathname,
    mealPlanListPage,
    mealPlansRefreshKey,
    shouldFetchGenericMealPlans,
    showClientChronology,
    usePaginatedMealPlanList,
  ]);

  React.useEffect(() => {
    if (genericLibraryOnly) {
      setActiveCategory('generic');
      return;
    }
    const queryCategory = new URLSearchParams(location.search).get('category')?.trim().toLowerCase();
    if (queryCategory === 'ai' || queryCategory === 'auto' || queryCategory === 'manual' || queryCategory === 'generic') {
      setActiveCategory(queryCategory as MealPlanCategory);
      setClientTimelineCategory(queryCategory === 'ai' || queryCategory === 'generic' ? queryCategory : 'manual');
    }
  }, [genericLibraryOnly, location.search]);

  const decoratedMealPlans = React.useMemo(() => {
    return mealPlans
      .map((item) => ({
        ...item,
        timelineStatus: classifyTimelineStatus(item.startDate, item.endDate),
        dateRangeLabel: formatDateRangeLabel(item.startDate, item.endDate),
      }))
      .sort((left, right) => {
        const leftTime = parseDateOnlyMillis(left.startDate) ?? 0;
        const rightTime = parseDateOnlyMillis(right.startDate) ?? 0;
        return leftTime - rightTime;
      });
  }, [mealPlans]);

  const clientTimelineCounts = React.useMemo(() => {
    return decoratedMealPlans.reduce(
      (counts, item) => {
        counts.all += 1;
        if (item.category === 'ai') {
          counts.ai += 1;
        } else if (item.category === 'generic') {
          counts.generic += 1;
        } else if (item.category === 'manual' || item.category === 'auto') {
          counts.manual += 1;
        }
        return counts;
      },
      { all: 0, manual: 0, generic: 0, ai: 0 } as Record<ClientTimelineCategory, number>
    );
  }, [decoratedMealPlans]);

  const filteredMealPlans = React.useMemo(() => {
    if (showClientChronology) {
      if (clientTimelineCategory === 'all') return decoratedMealPlans;
      if (clientTimelineCategory === 'manual') {
        return decoratedMealPlans.filter((item) => item.category === 'manual' || item.category === 'auto');
      }
      if (clientTimelineCategory === 'generic') return decoratedMealPlans.filter((item) => item.category === 'generic');
      return decoratedMealPlans.filter((item) => item.category === 'ai');
    }
    return decoratedMealPlans.filter((item) => item.category === activeCategory);
  }, [activeCategory, clientTimelineCategory, decoratedMealPlans, showClientChronology]);

  const listMealPlans = React.useMemo(() => {
    if (!usePaginatedMealPlanList) return filteredMealPlans;
    const paginatedMealPlans = mealPlans.map((item) => ({
      ...item,
      timelineStatus: classifyTimelineStatus(item.startDate, item.endDate),
      dateRangeLabel: formatDateRangeLabel(item.startDate, item.endDate),
    }));
    if (activeCategory === 'manual') {
      return paginatedMealPlans.filter((item) => item.category === 'manual' || item.category === 'auto');
    }
    return paginatedMealPlans.filter((item) => item.category === activeCategory);
  }, [activeCategory, filteredMealPlans, mealPlans, usePaginatedMealPlanList]);

  const timelineGroups = React.useMemo(() => ({
    future: filteredMealPlans.filter((item) => item.timelineStatus === 'future'),
    current: filteredMealPlans.filter((item) => item.timelineStatus === 'current'),
    past: filteredMealPlans.filter((item) => item.timelineStatus === 'past').reverse(),
  }), [filteredMealPlans]);
  const [timelineExpanded, setTimelineExpanded] = React.useState<Record<'future' | 'past', boolean>>({
    future: false,
    past: false,
  });

  const requestedMealPlanId = React.useMemo(
    () => new URLSearchParams(location.search).get('mealPlanId')?.trim() || '',
    [location.search]
  );
  const dismissedMealPlanIdRef = React.useRef('');

  React.useEffect(() => {
    if (!requestedMealPlanId || requestedMealPlanId !== dismissedMealPlanIdRef.current) {
      dismissedMealPlanIdRef.current = '';
    }
  }, [requestedMealPlanId]);

  const loadMealPlanExportData = React.useCallback(async (mealPlanId: string) => {
    try {
      const response = await getMealPlanExportForPdf(mealPlanId);
      if (response.success === false) {
        setMealPlanExportData(null);
        return;
      }
      setMealPlanExportData(unwrapMealPlanExportData(response.data ?? response));
    } catch (error) {
      console.error('Failed to load meal plan export payload:', error);
      setMealPlanExportData(null);
    }
  }, []);

  const openMealPlanDetails = React.useCallback(async (meal: MealPlanListItem) => {
    setActiveMealPlan(meal);
    setActiveMealPlanData(null);
    setMealPlanExportData(null);
    setMealPlanDetailsError('');
    setIsLoadingMealPlanDetails(true);

    try {
      const response = await getMealPlanDetailsForView(meal.id, meal.rawStatus === 'draft');
      if (meal.rawStatus !== 'draft') {
        void loadMealPlanExportData(meal.id);
      }
      if (response.success && response.data) {
        setActiveMealPlanData(response.data);
        return;
      }

      throw new Error(response.message || 'Unable to load meal plan details');
    } catch (error) {
      console.error('Failed to fetch meal plan details for details-v2 board:', error);
      setMealPlanDetailsError('Unable to load this meal plan right now.');
      showErrorToast(error, 'Unable to load meal plan');
    } finally {
      setIsLoadingMealPlanDetails(false);
    }
  }, [getMealPlanDetailsForView, loadMealPlanExportData]);

  const handleViewMealPlan = React.useCallback(async (meal: MealPlanListItem) => {
    const routeClientId = meal.sourceClientId || clientId;

    if (isGenericLibraryMealPlan(meal)) {
      const returnPath = genericLibraryOnly ? '/generic-meal-plans' : `${location.pathname}${location.search}`;
      const genericViewSearch = new URLSearchParams({ from: returnPath });
      if (routeClientId) {
        genericViewSearch.set('clientId', routeClientId);
      }
      navigate(`/meal-plans/generic/${encodeURIComponent(meal.id)}/nutrition/1?${genericViewSearch.toString()}`);
      return;
    }

    if (isAssignedGenericMealPlan(meal)) {
      if (!routeClientId) {
        showErrorToast(new Error('Client id is missing'), 'Unable to open meal plan');
        return;
      }
      const fromPath = `${location.pathname}${location.search}`;
      const detailSearch = new URLSearchParams({
        from: fromPath,
        genericAssigned: 'true',
      });
      navigate(`/meal-plans/${encodeURIComponent(meal.id)}/clients/${encodeURIComponent(routeClientId)}/nutrition/1?${detailSearch.toString()}`);
      return;
    }

    if (location.pathname === '/meal-planning' && routeClientId) {
      const params = new URLSearchParams({
        category: meal.category,
        mealPlanId: meal.id,
      });
      navigate(`/meal-planning/client/${encodeURIComponent(routeClientId)}/meal-plan?${params.toString()}`, {
        state: { mealPlanningViewMeal: meal },
      });
      return;
    }

    const isAiDraft = meal.category === 'ai' && meal.rawStatus === 'draft';
    if (isAiDraft) {
      const fromPath = `${window.location.pathname}${window.location.search}`;
      navigate(`/ai-meal-plan/draft/${meal.id}/client/${routeClientId}`, {
        state: {
          from: fromPath,
          planName: meal.title,
          initialStatus: 'draft',
        },
      });
      return;
    }

    await openMealPlanDetails(meal);
  }, [clientId, genericLibraryOnly, location.pathname, location.search, navigate, openMealPlanDetails]);

  React.useEffect(() => {
    const initialMealPlan = initialRouteMealPlanRef.current;
    if (!initialMealPlan) return;

    initialRouteMealPlanRef.current = null;
    void handleViewMealPlan(initialMealPlan);
  }, [handleViewMealPlan]);

  React.useEffect(() => {
    if (!requestedMealPlanId || loading || activeMealPlan?.id === requestedMealPlanId) return;
    if (dismissedMealPlanIdRef.current === requestedMealPlanId) return;
    const targetMealPlan = mealPlans.find((meal) => meal.id === requestedMealPlanId);
    if (!targetMealPlan) return;

    void handleViewMealPlan(targetMealPlan);
  }, [activeMealPlan?.id, handleViewMealPlan, loading, mealPlans, requestedMealPlanId]);

  const handleCloseMealPlanView = React.useCallback(() => {
    setActiveMealPlan(null);
    setActiveMealPlanData(null);
    setMealPlanExportData(null);
    setMealPlanDetailsError('');
    setIsLoadingMealPlanDetails(false);
    dismissedMealPlanIdRef.current = requestedMealPlanId;

    if (onMealPlanBack) {
      onMealPlanBack();
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search);
    const hadMealPlanParams = nextSearchParams.has('mealPlanId') || nextSearchParams.has('day');
    nextSearchParams.delete('mealPlanId');
    nextSearchParams.delete('day');

    if (hadMealPlanParams) {
      const nextSearch = nextSearchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate, onMealPlanBack, requestedMealPlanId]);

  /** Dashboard "Re-assign plan" lands with `?addMeal=1` on client details meal tab; open the same modal as "Add Meal Plan" then strip the param. */
  React.useEffect(() => {
    if (!location.pathname.includes('/details-v2/')) return;

    const params = new URLSearchParams(location.search);
    const flag = params.get('addMeal')?.trim().toLowerCase();
    if (flag !== '1' && flag !== 'true') return;

    handleCloseMealPlanView();

    const nextPlanType = isMealPlanningRoute && activeCategory === 'generic' ? 'manual' : activeCategory;
    setSelectedPlanType(nextPlanType);
    setNewMealName('');
    setAddMealPlanFieldErrors({});
    setAddMealPlanFormFeedback(null);
    if (nextPlanType === 'generic') {
      setSelectedGenericClinicalTags([]);
      setGenericCalorieTarget(DEFAULT_GENERIC_CALORIE_TARGET);
    }
    if (showClientSelection) {
      setSelectedClientQuery('');
      setSelectedClientId('');
      setIsClientDropdownOpen(false);
    }
    setIsAddMealModalOpen(true);

    params.delete('addMeal');
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true }
    );
  }, [activeCategory, handleCloseMealPlanView, isMealPlanningRoute, location.pathname, location.search, navigate, showClientSelection]);

  React.useEffect(() => {
    const needsClientOptions = isAddMealModalOpen && showClientSelection;
    if (!needsClientOptions) return;

    let isMounted = true;

    async function fetchClientsForModal() {
      const rawSearch = selectedClientQuery;
      const debouncedSearch = debouncedClientSearch;
      const searchValue = rawSearch.trim() ? debouncedSearch.trim() : '';
      setIsClientsLoading(true);
      try {
        const response = await getClients({
          page: 1,
          limit: 25,
          search: searchValue || undefined,
        });

        if (!isMounted) return;
        setClientOptions(Array.isArray(response.clients) ? response.clients : []);
      } catch (error) {
        console.error('Failed to fetch clients for meal plan modal:', error);
        if (isMounted) setClientOptions([]);
      } finally {
        if (isMounted) setIsClientsLoading(false);
      }
    }

    fetchClientsForModal();

    return () => {
      isMounted = false;
    };
  }, [debouncedClientSearch, isAddMealModalOpen, selectedClientQuery, showClientSelection]);

  const loadGenericClinicalTags = React.useCallback(async () => {
    if (isGenericClinicalTagsLoading || hasLoadedGenericClinicalTags) return;

    setIsGenericClinicalTagsLoading(true);
    try {
      const response = await getGenericClinicalTags();
      if (!response.success) {
        throw new Error(response.message || 'Unable to load clinical tags');
      }
      const tags = (response.data ?? [])
        .map((tag) => {
          const value = String(tag.value ?? tag.key ?? tag.id ?? tag.name ?? tag.label ?? '').trim();
          const label = String(tag.label ?? tag.name ?? tag.value ?? tag.key ?? tag.id ?? '').trim();
          return value ? { value, label: label || value } : null;
        })
        .filter((tag): tag is { value: string; label: string } => tag !== null);
      setGenericClinicalTags(tags);
      setHasLoadedGenericClinicalTags(true);
    } catch (error) {
      setGenericClinicalTags([]);
      setHasLoadedGenericClinicalTags(true);
      setAddMealPlanFormFeedback({
        variant: 'error',
        title: 'Unable to load clinical tags',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsGenericClinicalTagsLoading(false);
    }
  }, [hasLoadedGenericClinicalTags, isGenericClinicalTagsLoading]);

  const loadGenericMealSlotsForClient = React.useCallback(async (targetClientId: string) => {
    const normalizedClientId = targetClientId.trim();
    if (!normalizedClientId) return toGenericMealPlanSlots([]);

    try {
      const response = await getMealPrograms(normalizedClientId);
      return toGenericMealPlanSlots(response?.data?.meals);
    } catch (error) {
      if (isNotFoundError(error)) {
        return toGenericMealPlanSlots([]);
      }
      throw error;
    }
  }, []);


  const getClientDisplayName = React.useCallback((client: Client) => {
    return client.fullName?.trim() || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed Client';
  }, []);

  const getClientConditionSummary = React.useCallback((client: Client) => {
    const condition = typeof client.medicalConditions === 'string' ? client.medicalConditions.trim() : '';
    return condition ? formatDelimitedDisplayLabel(condition) : '';
  }, []);

  const getCreatedMealPlanId = React.useCallback((response: unknown): string => {
    const payload = response && typeof response === 'object' ? (response as Record<string, unknown>) : {};
    const data = payload.data && typeof payload.data === 'object'
      ? (payload.data as Record<string, unknown>)
      : {};
    return String(
        data.mealPlanId ??
        data.planId ??
        data.draftId ??
        data.id ??
        payload.mealPlanId ??
        payload.planId ??
        payload.draftId ??
        payload.id ??
        ''
    ).trim();
  }, []);

  const handleSubmitNewMealPlan = React.useCallback(() => {
    const routeByType: Record<MealPlanCategory, string> = {
      manual: '/manual-meal-plan',
      ai: '/ai-meal-planning',
      // Legacy "auto" selection should use the new AI flow UI.
      auto: '/ai-meal-planning',
      generic: '/manual-meal-plan',
    };

    const params = new URLSearchParams();
    const targetClientId = showClientSelection ? (selectedClientId || clientId) : clientId;
    const planName = newMealName.trim();
    const nextFieldErrors: typeof addMealPlanFieldErrors = {};
    if (!planName) {
      nextFieldErrors.mealName = 'Please enter a meal plan name.';
    }
    if ((selectedPlanType === 'ai' || selectedPlanType === 'auto') && newMealDays > AI_MEAL_PLAN_MAX_DAYS) {
      nextFieldErrors.days = `AI meal plans are limited to ${AI_MEAL_PLAN_MAX_DAYS} days.`;
    }
    if (selectedPlanType === 'generic') {
      if (!genericLibraryOnly && !targetClientId) {
        nextFieldErrors.client = 'Please select a client to create a cohort meal plan.';
      }
      if (!selectedGenericClinicalTags.length) {
        nextFieldErrors.clinicalTags = 'Please select at least one clinical tag.';
      }
      if (genericCalorieTarget.mode === 'direct') {
        const dailyCalories = Number(genericCalorieTarget.dailyCalories);
        if (!Number.isFinite(dailyCalories) || dailyCalories < 500 || dailyCalories > 6000) {
          nextFieldErrors.calorieTarget = 'Enter a daily target between 500 and 6,000 kcal.';
        }
      } else {
        const ageYears = Number(genericCalorieTarget.ageYears);
        const weightKg = Number(genericCalorieTarget.weightKg);
        const heightCm = Number(genericCalorieTarget.heightCm);
        if (!Number.isInteger(ageYears) || ageYears < 1 || ageYears > 120) {
          nextFieldErrors.ageYears = 'Enter an age from 1 to 120.';
        }
        if (!genericCalorieTarget.gender) {
          nextFieldErrors.gender = 'Select the sex used by the EER formula.';
        }
        if (!Number.isFinite(weightKg) || weightKg < 1 || weightKg > 500) {
          nextFieldErrors.weightKg = 'Enter a weight from 1 to 500 kg.';
        }
        if (!Number.isFinite(heightCm) || heightCm < 30 || heightCm > 250) {
          nextFieldErrors.heightCm = 'Enter a height from 30 to 250 cm.';
        }
        if (!genericCalorieTarget.activityLevel) {
          nextFieldErrors.activityLevel = 'Select an activity level.';
        }
      }

      if (Object.keys(nextFieldErrors).length > 0) {
        setAddMealPlanFieldErrors(nextFieldErrors);
        setAddMealPlanFormFeedback(null);
        return;
      }

      setAddMealPlanFieldErrors({});
      setAddMealPlanFormFeedback(null);
      setIsCreatingGenericMealPlan(true);
      void (async () => {
        try {
          const mealSlots = genericLibraryOnly ? [] : await loadGenericMealSlotsForClient(targetClientId);
          const response = await createGenericMealPlan({
            ...(targetClientId ? { clientId: targetClientId } : {}),
            name: planName,
            description: '',
            clinicalTags: selectedGenericClinicalTags,
            calorieTarget: genericCalorieTarget.mode === 'direct'
              ? {
                mode: 'direct',
                dailyCalories: Number(genericCalorieTarget.dailyCalories),
              }
              : {
                mode: 'reference_person',
                referencePerson: {
                  ageYears: Number(genericCalorieTarget.ageYears),
                  gender: genericCalorieTarget.gender as 'male' | 'female',
                  weightKg: Number(genericCalorieTarget.weightKg),
                  heightCm: Number(genericCalorieTarget.heightCm),
                  activityLevel: genericCalorieTarget.activityLevel,
                  bmrFormulaId: 'iom',
                  weightGoal: genericCalorieTarget.weightGoal,
                },
              },
            mealSlots,
          });
          if (!response.success) {
            throw new Error(response.message || 'Unable to create cohort meal plan');
          }

          const createdPlanId = getCreatedMealPlanId(response);
          setIsAddMealModalOpen(false);
          setActiveCategory('generic');
          setMealPlansRefreshKey((value) => value + 1);
          if (!createdPlanId) {
            showSuccessToast('Cohort meal plan created');
            return;
          }

          if (genericLibraryOnly) {
            const genericViewSearch = new URLSearchParams({ from: '/generic-meal-plans' });
            navigate(`/meal-plans/generic/${encodeURIComponent(createdPlanId)}/nutrition/1?${genericViewSearch.toString()}`);
            showSuccessToast('Cohort meal plan created');
            return;
          }

          const detailsResponse = await getGenericMealPlanForDisplay(createdPlanId);
          if (detailsResponse.success && detailsResponse.data) {
            const selectedGenericClinicalTagLabels = selectedGenericClinicalTags
              .map((tag) => genericClinicalTags.find((option) => option.value === tag)?.label || formatApiTag(tag))
              .filter(Boolean);
            setActiveMealPlan({
              id: createdPlanId,
              category: 'generic',
              title: planName,
              durationLabel: `${mealSlots.length} Meal Slot${mealSlots.length === 1 ? '' : 's'}`,
              tag: defaultByCategory.generic.tag,
              tags: selectedGenericClinicalTagLabels,
              calories: null,
              carbs: null,
              protein: null,
              fat: null,
              assignedClients: null,
              status: 'Draft',
              rawStatus: 'generic',
              planType: 'generic',
              generationType: 'generic',
              creationMethod: 'generic',
              type: 'generic',
            });
            setActiveMealPlanData(detailsResponse.data);
          }
          showSuccessToast('Cohort meal plan created');
        } catch (error) {
          setAddMealPlanFormFeedback({
            variant: 'error',
            title: 'Unable to create cohort meal plan',
            message: extractErrorMessage(error),
          });
        } finally {
          setIsCreatingGenericMealPlan(false);
        }
      })();
      return;
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setAddMealPlanFieldErrors(nextFieldErrors);
      setAddMealPlanFormFeedback(null);
      return;
    }

    setAddMealPlanFieldErrors({});
    setAddMealPlanFormFeedback(null);
    if (targetClientId) params.set('clientId', targetClientId);
    params.set('name', planName);
    if (Number.isFinite(newMealDays)) params.set('days', String(newMealDays));
    if (selectedPlanType === 'manual') {
      const returnPath = `${window.location.pathname}${window.location.search}`;
      if (returnPath) params.set('from', returnPath);
      trackManualMealPlanSetupStarted({
        ...getMealPlanEntryProperties(),
        has_target_client: Boolean(targetClientId),
        plan_name_provided: Boolean(planName),
        requested_days: newMealDays,
        selected_meal_plan_type: selectedPlanType,
      });
    }
    if (selectedPlanType === 'ai' || selectedPlanType === 'auto') {
      const returnPath = `${window.location.pathname}${window.location.search}`;
      if (returnPath) params.set('from', returnPath);
      trackAIMealPlanSetupStarted({
        ...getMealPlanEntryProperties(),
        has_target_client: Boolean(targetClientId),
        plan_name_provided: Boolean(planName),
        requested_days: newMealDays,
        selected_meal_plan_type: selectedPlanType,
      });
    }

    navigate(`${routeByType[selectedPlanType]}?${params.toString()}`);
    setIsAddMealModalOpen(false);
  }, [
    clientId,
    genericLibraryOnly,
    genericCalorieTarget,
    genericClinicalTags,
    getCreatedMealPlanId,
    getMealPlanEntryProperties,
    loadGenericMealSlotsForClient,
    navigate,
    newMealDays,
    newMealName,
    selectedClientId,
    selectedGenericClinicalTags,
    selectedPlanType,
    showClientSelection,
  ]);

  const handleEditMealDay = React.useCallback(
    (dayNumber: number) => {
      if (!activeMealPlan?.id || !clientId) return;
      const returnPath = `${window.location.pathname}${window.location.search}`;
      const url = `/meal-plans/${activeMealPlan.id}/clients/${clientId}/nutrition/${dayNumber}?from=${encodeURIComponent(returnPath)}`;
      navigate(url);
    },
    [activeMealPlan?.id, clientId, navigate]
  );

  const handleDeleteMealDay = React.useCallback(
    async (dayNumber: number): Promise<boolean> => {
      if (!activeMealPlan?.id) return false;

      trackMealPlanDayDeleteClicked({
        product_area: 'meal_plans',
        meal_plan_surface: 'board_view',
      });

      setIsDeletingDay(true);
      try {
        await deleteMealPlanDay(activeMealPlan.id, dayNumber);

        const response = await getMealPlanDetailsForView(activeMealPlan.id, activeMealPlan.rawStatus === 'draft');
        if (response.success && response.data) {
          setActiveMealPlanData(response.data);
          return true;
        }

        throw new Error(response.message || 'Unable to load meal plan details');
      } catch (error) {
        showErrorToast(error, 'Unable to delete day');
        return false;
      } finally {
        setIsDeletingDay(false);
      }
    },
    [activeMealPlan?.id, activeMealPlan?.rawStatus, getMealPlanDetailsForView]
  );

  const handleSaveMealDayName = React.useCallback(
    async (dayNumber: number, dayName: string | null): Promise<boolean> => {
      if (!activeMealPlan?.id) return false;

      setIsSavingDayName(true);
      try {
        const response = await updateMealPlanDayName(activeMealPlan.id, { day: dayNumber, dayName });
        if (response?.success === false) {
          throw new Error(response?.message || 'Unable to update day name');
        }

        setActiveMealPlanData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            days: prev.days.map((day) => {
              if (day.day !== dayNumber) return day;
              return {
                ...day,
                dayName: dayName ?? undefined,
              };
            }),
          };
        });
        return true;
      } catch (error) {
        showErrorToast(error, 'Unable to update day name');
        return false;
      } finally {
        setIsSavingDayName(false);
      }
    },
    [activeMealPlan?.id]
  );

  const applyLocalMealPlanNotes = React.useCallback(
    (request: UpdateMealPlanNotesRequest) => {
      setActiveMealPlanData((prev) => {
        if (!prev) return prev;
        const nextNotes =
          request.notes === undefined
            ? undefined
            : request.notes == null || String(request.notes).trim() === ''
              ? null
              : String(request.notes);

        if (request.level === 'plan') {
          if (prev.status === 'draft') {
            return {
              ...prev,
              planNotes: nextNotes === undefined ? prev.planNotes : nextNotes,
            };
          }
          return {
            ...prev,
            description: nextNotes === undefined ? prev.description : nextNotes,
          };
        }

        if (request.level === 'day') {
          return {
            ...prev,
            days: prev.days.map((day) => {
              if (day.day !== request.day) return day;
              return {
                ...day,
                ...(request.dayName !== undefined ? { dayName: request.dayName } : {}),
                ...(nextNotes !== undefined ? { notes: nextNotes } : {}),
              };
            }),
          };
        }

        if (request.level === 'meal') {
          return {
            ...prev,
            days: prev.days.map((day) => {
              if (day.day !== request.day) return day;
              const mealKey = Object.keys(day.meals || {}).find((key) => {
                const meal = day.meals[key] as { mealName?: string } | undefined;
                const mealName = String(meal?.mealName ?? key).trim();
                return mealName === request.mealName || key === request.mealName;
              });
              if (!mealKey || !day.meals[mealKey]) return day;
              return {
                ...day,
                meals: {
                  ...day.meals,
                  [mealKey]: {
                    ...day.meals[mealKey],
                    ...(nextNotes !== undefined ? { notes: nextNotes } : {}),
                  },
                },
              };
            }),
          };
        }

        // item
        const itemId = 'itemId' in request ? request.itemId : undefined;
        const recipeId = 'recipeId' in request ? request.recipeId : undefined;
        const dayNumber = 'day' in request ? request.day : undefined;
        const mealName = 'mealName' in request ? request.mealName : undefined;

        return {
          ...prev,
          days: prev.days.map((day) => {
            if (dayNumber != null && day.day !== dayNumber) return day;
            const nextMeals = { ...day.meals };
            let changed = false;
            Object.entries(nextMeals).forEach(([key, meal]) => {
              if (!meal) return;
              if (mealName) {
                const slotName = String(meal.mealName ?? key).trim();
                if (slotName !== mealName && key !== mealName) return;
              }
              const recipes = Array.isArray(meal.recipes) ? meal.recipes : [meal];
              const nextRecipes = recipes.map((recipe) => {
                const matchesItem = itemId && recipe.itemId === itemId;
                const matchesRecipe = recipeId && recipe.recipeId === recipeId;
                if (!matchesItem && !matchesRecipe) return recipe;
                changed = true;
                return {
                  ...recipe,
                  ...(nextNotes !== undefined ? { customNotes: nextNotes ?? undefined } : {}),
                };
              });
              if (!changed) return;
              nextMeals[key] = {
                ...meal,
                recipes: nextRecipes,
                ...(meal.recipeId && recipeId && meal.recipeId === recipeId && nextNotes !== undefined
                  ? { customNotes: nextNotes ?? undefined }
                  : {}),
                ...(meal.itemId && itemId && meal.itemId === itemId && nextNotes !== undefined
                  ? { customNotes: nextNotes ?? undefined }
                  : {}),
              };
            });
            return changed ? { ...day, meals: nextMeals } : day;
          }),
        };
      });
    },
    [],
  );

  const handleSaveMealPlanNotes = React.useCallback(
    async (request: UpdateMealPlanNotesRequest): Promise<boolean> => {
      if (!activeMealPlan?.id) return false;

      setIsSavingMealPlanNotes(true);
      try {
        const response = await updateMealPlanNotes(activeMealPlan.id, request);
        if (response?.success === false) {
          throw new Error(response?.message || 'Unable to update notes');
        }

        applyLocalMealPlanNotes(request);

        // Reconcile from GET; AI drafts may not always echo every notes field.
        let showedDraftEchoWarning = false;
        try {
          const refreshed = await getMealPlanDetailsForView(
            activeMealPlan.id,
            activeMealPlan.rawStatus === 'draft',
          );
          if (refreshed.success && refreshed.data) {
            setActiveMealPlanData(refreshed.data);
            if (request.level !== 'plan' && request.notes != null && String(request.notes).trim()) {
              const expected = String(request.notes).trim();
              let found = false;
              if (request.level === 'day') {
                found = refreshed.data.days.some(
                  (day) => day.day === request.day && String(day.notes ?? '').trim() === expected,
                );
              } else if (request.level === 'meal') {
                found = refreshed.data.days.some((day) => {
                  if (day.day !== request.day) return false;
                  return Object.entries(day.meals || {}).some(([key, meal]) => {
                    const mealName = String(meal.mealName ?? key).trim();
                    return (
                      (mealName === request.mealName || key === request.mealName) &&
                      String(meal.notes ?? '').trim() === expected
                    );
                  });
                });
              } else if (request.level === 'item') {
                found = refreshed.data.days.some((day) =>
                  Object.values(day.meals || {}).some((meal) => {
                    const recipes = Array.isArray(meal.recipes) ? meal.recipes : [meal];
                    return recipes.some((recipe) => {
                      if ('itemId' in request && request.itemId) {
                        return recipe.itemId === request.itemId && String(recipe.customNotes ?? '').trim() === expected;
                      }
                      if ('recipeId' in request && request.recipeId) {
                        return recipe.recipeId === request.recipeId && String(recipe.customNotes ?? '').trim() === expected;
                      }
                      return false;
                    });
                  }),
                );
              }
              if (!found && activeMealPlan.rawStatus === 'draft') {
                showedDraftEchoWarning = true;
                showWarningToast(
                  'Notes saved',
                  'The note was saved, but this draft response did not return it yet. Try refreshing if it does not appear.',
                );
              }
            }
          }
        } catch {
          // Keep optimistic local update if refetch fails.
        }

        if (activeMealPlan.rawStatus !== 'draft') {
          void loadMealPlanExportData(activeMealPlan.id);
        }

        if (!showedDraftEchoWarning) {
          showNotesSavedToast(request.level, request.notes);
        }
        return true;
      } catch (error) {
        showErrorToast(error, 'Unable to update notes');
        return false;
      } finally {
        setIsSavingMealPlanNotes(false);
      }
    },
    [
      activeMealPlan?.id,
      activeMealPlan?.rawStatus,
      applyLocalMealPlanNotes,
      getMealPlanDetailsForView,
      loadMealPlanExportData,
    ],
  );

  const handleReorderMealDays = React.useCallback(
    async (dayOrder: number[]): Promise<boolean> => {
      if (!activeMealPlan?.id) return false;

      // Optimistic update
      setActiveMealPlanData((prev) => {
        if (!prev) return prev;
        const newDays = [...prev.days];
        newDays.sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));
        return { ...prev, days: newDays };
      });

      setIsReorderingDays(true);
      try {
        await reorderMealPlanDays(activeMealPlan.id, { dayOrder });

        const response = await getMealPlanDetailsForView(activeMealPlan.id, activeMealPlan.rawStatus === 'draft');
        if (response.success && response.data) {
          setActiveMealPlanData(response.data);
          return true;
        }

        throw new Error(response.message || 'Unable to load meal plan details');
      } catch (error) {
        showErrorToast(error, 'Unable to reorder days');
        // Re-fetch to revert optimistic update on failure
        getMealPlanDetailsForView(activeMealPlan.id, activeMealPlan.rawStatus === 'draft').then((res) => {
          if (res.success && res.data) setActiveMealPlanData(res.data);
        });
        return false;
      } finally {
        setIsReorderingDays(false);
      }
    },
    [activeMealPlan?.id, activeMealPlan?.rawStatus, getMealPlanDetailsForView]
  );

  const handleReorderMealsInDay = React.useCallback(
    async (
      dayNumber: number,
      orderedMealOrders: number[],
      mealSwap?: {
        firstMealName: string;
        secondMealName: string;
        firstMealOrder?: number;
        secondMealOrder?: number;
      }
    ): Promise<boolean> => {
      if (!activeMealPlan?.id) return false;

      const isFinalizedPlan = activeMealPlan.rawStatus !== 'draft';
      const firstMealName = normalizeSwapMealName(String(mealSwap?.firstMealName ?? ''));
      const secondMealName = normalizeSwapMealName(String(mealSwap?.secondMealName ?? ''));
      const hasNamedSwapPayload =
        Boolean(firstMealName) &&
        Boolean(secondMealName) &&
        firstMealName !== secondMealName;
      const firstMealOrder = Number(mealSwap?.firstMealOrder);
      const secondMealOrder = Number(mealSwap?.secondMealOrder);
      const hasOrderSwapPayload =
        Number.isFinite(firstMealOrder) &&
        Number.isFinite(secondMealOrder) &&
        firstMealOrder > 0 &&
        secondMealOrder > 0 &&
        firstMealOrder !== secondMealOrder;
      const hasRecognizedFinalizedNamePayload =
        recognizedFinalizedMealTypes.has(firstMealName) &&
        recognizedFinalizedMealTypes.has(secondMealName);
      const hasDirectSwapPayload = isFinalizedPlan
        ? hasOrderSwapPayload || (hasNamedSwapPayload && hasRecognizedFinalizedNamePayload)
        : hasNamedSwapPayload;

      // Keep swap-meals server-driven to avoid local tag/content mismatch while dragging.
      if (!hasDirectSwapPayload) {
        setActiveMealPlanData((prev) => {
          if (!prev) return prev;
          const newDays = [...prev.days];
          const dayIndex = newDays.findIndex((d) => d.day === dayNumber);
          if (dayIndex !== -1 && newDays[dayIndex].meals) {
            const meals = { ...newDays[dayIndex].meals };

            // Sort keys using logic similar to getMealEntries to ensure alignment with board view
            const getMealOrderVal = (meal: any, key: string) => {
              if (typeof meal.slotOrder === 'number' && meal.slotOrder > 0) return meal.slotOrder;
              if (typeof meal.mealOrder === 'number' && meal.mealOrder > 0) return meal.mealOrder;
              if (typeof meal.sortOrder === 'number' && meal.sortOrder > 0) return meal.sortOrder;
              const normalized = key.toLowerCase().replace(/\s+/g, '-');
              const fallbackMap: Record<string, number> = {
                'pre-breakfast': 1, 'breakfast': 2, 'mid-morning-snack': 3,
                'lunch': 4, 'afternoon-snack': 5, 'dinner': 6, 'post-dinner-snack': 7
              };
              return fallbackMap[normalized] ?? 999;
            };

            const mealKeys = Object.keys(meals).sort((a, b) => getMealOrderVal(meals[a], a) - getMealOrderVal(meals[b], b));
            if (mealKeys.length === orderedMealOrders.length) {
              mealKeys.forEach((key, idx) => {
                meals[key] = { ...meals[key], slotOrder: orderedMealOrders[idx] };
              });
              newDays[dayIndex] = { ...newDays[dayIndex], meals };
            }
          }
          return { ...prev, days: newDays };
        });
      }

      setIsReorderingMeals(true);
      try {
        if (hasDirectSwapPayload) {
          if (isFinalizedPlan && hasOrderSwapPayload) {
            await swapMealPlanMeals(activeMealPlan.id, {
              day: dayNumber,
              firstMealOrder,
              secondMealOrder,
            });
          } else {
            await swapMealPlanMeals(activeMealPlan.id, {
              day: dayNumber,
              firstMealName,
              secondMealName,
            });
          }
        } else if (activeMealPlan.rawStatus === 'draft') {
          await reorderManualMealSlots(activeMealPlan.id, dayNumber, orderedMealOrders);
        } else {
          await reorderMealPlanMeals(activeMealPlan.id, { day: dayNumber, orderedMealOrders });
        }

        const response = await getMealPlanDetailsForView(activeMealPlan.id, activeMealPlan.rawStatus === 'draft');
        if (response.success && response.data) {
          setActiveMealPlanData(response.data);
          return true;
        }

        throw new Error(response.message || 'Unable to load meal plan details');
      } catch (error) {
        showErrorToast(error, 'Unable to reorder meal slots');
        // Re-fetch to revert optimistic update on failure
        getMealPlanDetailsForView(activeMealPlan.id, activeMealPlan.rawStatus === 'draft').then((res) => {
          if (res.success && res.data) setActiveMealPlanData(res.data);
        });
        return false;
      } finally {
        setIsReorderingMeals(false);
      }
    },
    [activeMealPlan?.id, activeMealPlan?.rawStatus, getMealPlanDetailsForView, normalizeSwapMealName]
  );

  const handleAddDay = React.useCallback(
    async (payload: AddDayPayload) => {
      if (!activeMealPlan?.id || !activeMealPlanData) return;

      const sortedDays = [...activeMealPlanData.days].sort((left, right) => left.day - right.day);
      const nextDayNumber = Math.max(1, sortedDays.reduce((maxValue, currentDay) => Math.max(maxValue, currentDay.day || 0), 0) + 1);
      const defaultNextDayName = `Day ${nextDayNumber}`;
      const normalizedDayNameInput = payload.dayName.trim();
      const sourceDay = Number(payload.copyFromId);
      const templateId = String(payload.templateId ?? '').trim();
      if (payload.mode === 'copy' && (!Number.isFinite(sourceDay) || sourceDay <= 0)) {
        showErrorToast(new Error('Please select a day to copy from'), 'Unable to copy day');
        return;
      }
      if (payload.mode === 'import' && !templateId) {
        showErrorToast(new Error('Please select a template to import'), 'Unable to import template');
        return;
      }
      const shouldUpdateCustomDayName = Boolean(normalizedDayNameInput) && normalizedDayNameInput !== defaultNextDayName;
      const startDate = (activeMealPlanData.searchParams?.startDate || sortedDays[0]?.date || '').slice(0, 10);
      const searchParamsRecord = (activeMealPlanData.searchParams ?? {}) as Record<string, unknown>;
      const endDate = (String(searchParamsRecord.endDate ?? sortedDays[sortedDays.length - 1]?.date ?? startDate)).slice(0, 10);
      if (!startDate || !endDate) return;

      const [year, month, day] = endDate.split('-').map((value) => Number(value));
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return;
      const nextDate = new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);

      try {
        await changeMealPlanDates(activeMealPlan.id, {
          newStartDate: startDate,
          newEndDate: nextDate,
        });

        if (payload.mode === 'copy') {
          await copyManualMealPlanDay({
            mealPlanId: activeMealPlan.id,
            sourceDay,
            targetDays: [nextDayNumber],
          });
        } else if (payload.mode === 'import') {
          const restrictionWarning = buildMealPlanRestrictionWarning(payload.template, client, 'template');
          if (restrictionWarning) {
            showWarningToast('Check client restrictions', restrictionWarning.message);
          }
          const importResponse = await importMealPlanTemplate(activeMealPlan.id, {
            templateId,
            day: nextDayNumber,
            templateDay: 1,
          });
          if (!importResponse.success) {
            throw new Error(importResponse.message || 'Unable to import template');
          }
        }

        const response = await getMealPlanDetailsForView(activeMealPlan.id, activeMealPlan.rawStatus === 'draft');
        if (response.success && response.data) {
          let nextMealPlanData = response.data;
          setActiveMealPlanData(nextMealPlanData);

          if (shouldUpdateCustomDayName) {
            const dayNameResponse = await updateMealPlanDayName(activeMealPlan.id, {
              day: nextDayNumber,
              dayName: normalizedDayNameInput,
            });
            if (dayNameResponse?.success === false) {
              throw new Error(dayNameResponse?.message || 'Unable to update day name');
            }

            nextMealPlanData = {
              ...nextMealPlanData,
              days: nextMealPlanData.days.map((dayData) => {
                if (dayData.day !== nextDayNumber) return dayData;
                return {
                  ...dayData,
                  dayName: normalizedDayNameInput,
                };
              }),
            };
            setActiveMealPlanData(nextMealPlanData);
          }

          return;
        }

        throw new Error(response.message || 'Unable to load meal plan details');
      } catch (error) {
        showErrorToast(
          error,
          payload.mode === 'copy'
            ? 'Unable to copy day'
            : payload.mode === 'import'
              ? 'Unable to import template'
              : 'Unable to add blank day'
        );
      }
    },
    [activeMealPlan?.id, activeMealPlan?.rawStatus, activeMealPlanData, client, getMealPlanDetailsForView]
  );

  const refreshActiveMealPlanData = React.useCallback(async () => {
    if (!activeMealPlan?.id) return;
    const response = isGenericLibraryMealPlan(activeMealPlan)
      ? await getGenericMealPlanForDisplay(activeMealPlan.id)
      : await getMealPlanDetailsForView(activeMealPlan.id, activeMealPlan.rawStatus === 'draft');
    if (activeMealPlan.rawStatus !== 'draft') {
      void loadMealPlanExportData(activeMealPlan.id);
    }
    if (response.success && response.data) {
      setActiveMealPlanData(response.data);
    }
  }, [
    activeMealPlan?.id,
    activeMealPlan?.rawStatus,
    getMealPlanDetailsForView,
    loadMealPlanExportData,
  ]);

  const handleCopyDay = React.useCallback(
    async (payload: { sourceDay: number; targetDays: number[] }) => {
      if (!activeMealPlan?.id) return;
      const normalizedTargetDays = Array.from(
        new Set(
          (payload.targetDays ?? [])
            .map((day) => Number(day))
            .filter((day) => Number.isFinite(day) && day > 0)
        )
      );
      if (!normalizedTargetDays.length) {
        const err = new Error('Choose at least one target day');
        showErrorToast(err, 'Unable to copy day');
        throw err;
      }
      if (normalizedTargetDays.includes(payload.sourceDay)) {
        const err = new Error('Choose a different day to copy from');
        showErrorToast(err, 'Unable to copy day');
        throw err;
      }

      setIsCopyingDay(true);
      try {
        await copyManualMealPlanDay({
          mealPlanId: activeMealPlan.id,
          sourceDay: payload.sourceDay,
          targetDays: normalizedTargetDays,
        });
        const response = await getMealPlanDetailsForView(activeMealPlan.id, activeMealPlan.rawStatus === 'draft');
        if (response.success && response.data) {
          setActiveMealPlanData(response.data);
          return;
        }
        throw new Error(response.message || 'Unable to load meal plan details');
      } catch (error) {
        showErrorToast(error, 'Unable to copy day');
        throw error;
      } finally {
        setIsCopyingDay(false);
      }
    },
    [activeMealPlan?.id, activeMealPlan?.rawStatus, getMealPlanDetailsForView]
  );

  const handleDeleteMealPlan = React.useCallback((meal: MealPlanListItem) => {
    if (!meal.id || deletingMealPlanId === meal.id) return;
    setPendingDeleteMealPlan(meal);
    setDeleteConfirmOpen(true);
  }, [deletingMealPlanId]);

  const closeDeleteConfirmDialog = React.useCallback(() => {
    if (deletingMealPlanId) return;
    setDeleteConfirmOpen(false);
    setPendingDeleteMealPlan(null);
  }, [deletingMealPlanId]);

  const handleDeleteMealPlanConfirm = React.useCallback(async () => {
    const meal = pendingDeleteMealPlan;
    if (!meal?.id) return;

    const isDraft = meal.rawStatus === 'draft';
    const isGeneric = isGenericLibraryMealPlan(meal);

    setDeletingMealPlanId(meal.id);
    try {
      const response = isGeneric
        ? await deleteGenericMealPlan(meal.id)
        : isDraft
          ? await deleteDraft(meal.id)
          : await deleteMealPlan(meal.id);
      if (response?.success === false) {
        throw new Error(response?.message || 'Failed to delete meal plan');
      }

      setMealPlans((previous) => previous.filter((item) => item.id !== meal.id));

      if (activeMealPlan?.id === meal.id) {
        setActiveMealPlan(null);
        setActiveMealPlanData(null);
        setMealPlanDetailsError('');
      }

      showSuccessToast(
        isGeneric ? 'Cohort meal plan deleted' : isDraft ? 'Draft deleted' : 'Meal plan archived',
        response?.message || (isGeneric ? 'Cohort meal plan removed.' : isDraft ? 'Draft moved to expired.' : 'Meal plan moved to archived.')
      );
    } catch (error) {
      showErrorToast(error, 'Unable to delete meal plan');
    } finally {
      setDeletingMealPlanId((current) => (current === meal.id ? null : current));
      setDeleteConfirmOpen(false);
      setPendingDeleteMealPlan(null);
    }
  }, [activeMealPlan?.id, pendingDeleteMealPlan]);

  const openAssignGenericDialog = React.useCallback((meal: MealPlanListItem) => {
    const today = getTodayDateInputValue();
    setAssignGenericPlan(meal);
    setAssignClientId('');
    setAssignStartDate(today);
    setAssignEndDate(today);
    setAssignDateConflicts([]);
    setAssignGenericFieldErrors({});
    setAssignGenericFormFeedback(null);
  }, []);

  const closeAssignGenericDialog = React.useCallback(() => {
    if (isAssigningGenericPlan) return;
    setAssignGenericPlan(null);
    setAssignStartDate('');
    setAssignEndDate('');
    setAssignDateConflicts([]);
    setAssignGenericFieldErrors({});
    setAssignGenericFormFeedback(null);
  }, [isAssigningGenericPlan]);

  const handleAssignStartDateChange = React.useCallback((value: string) => {
    const nextStartDate = value.trim();
    setAssignStartDate(nextStartDate);
    setAssignEndDate((currentEndDate) => clampAssignmentEndDate(nextStartDate, currentEndDate));
    setAssignDateConflicts([]);
    setAssignGenericFieldErrors((current) => ({
      ...current,
      startDate: undefined,
      endDate: undefined,
    }));
    setAssignGenericFormFeedback(null);
  }, []);

  const handleAssignEndDateChange = React.useCallback((value: string) => {
    setAssignEndDate(value.trim());
    setAssignDateConflicts([]);
    setAssignGenericFieldErrors((current) => ({
      ...current,
      endDate: undefined,
    }));
    setAssignGenericFormFeedback(null);
  }, []);

  const submitAssignGenericPlan = React.useCallback(async (overwriteConflicts = false) => {
    if (!assignGenericPlan?.id) return;
    const normalizedStartDate = assignStartDate.trim();
    const normalizedEndDate = assignEndDate.trim();
    const todayTime = parseDateOnlyMillis(getTodayDateInputValue()) ?? 0;
    const startTime = parseDateOnlyMillis(normalizedStartDate);
    const endTime = parseDateOnlyMillis(normalizedEndDate);
    const nextFieldErrors: { client?: string; startDate?: string; endDate?: string } = {};
    if (!assignClientId) {
      nextFieldErrors.client = 'Please select a client.';
    }
    if (!normalizedStartDate) {
      nextFieldErrors.startDate = 'Please select a start date.';
    } else if (startTime == null) {
      nextFieldErrors.startDate = 'Please select a valid start date.';
    } else if (startTime < todayTime) {
      nextFieldErrors.startDate = 'Start date cannot be in the past.';
    }
    if (!normalizedEndDate) {
      nextFieldErrors.endDate = 'Please select an end date.';
    } else if (endTime == null) {
      nextFieldErrors.endDate = 'Please select a valid end date.';
    } else if (endTime < todayTime) {
      nextFieldErrors.endDate = 'End date cannot be in the past.';
    }

    if (!nextFieldErrors.startDate && !nextFieldErrors.endDate && startTime != null && endTime != null) {
      const rangeDays = getInclusiveDateRangeDays(normalizedStartDate, normalizedEndDate);
      if (endTime < startTime) {
        nextFieldErrors.endDate = 'End date cannot be before start date.';
      } else if (rangeDays != null && rangeDays > MEAL_PLAN_MAX_DAYS) {
        nextFieldErrors.endDate = `Meal plans cannot exceed ${MEAL_PLAN_MAX_DAYS} days.`;
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setAssignGenericFieldErrors(nextFieldErrors);
      setAssignGenericFormFeedback(null);
      return;
    }

    setAssignGenericFieldErrors({});
    setAssignGenericFormFeedback(null);
    setIsAssigningGenericPlan(true);
    try {
      const response = await assignGenericMealPlan({
        genericPlanId: assignGenericPlan.id,
        clientId: assignClientId,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        overwriteConflicts,
      });
      if (!response.success) {
        throw new Error(response.message || 'Unable to assign cohort meal plan');
      }
      showSuccessToast('Cohort meal plan assigned');
      setAssignGenericPlan(null);
      setAssignDateConflicts([]);
      setAssignGenericFormFeedback(null);
      setMealPlansRefreshKey((value) => value + 1);
    } catch (error) {
      const conflicts = extractMealPlanDateConflicts(error);
      if (conflicts.length > 0) {
        setAssignDateConflicts(conflicts);
        setAssignGenericFormFeedback(null);
        return;
      }
      setAssignGenericFormFeedback({
        variant: 'error',
        title: 'Unable to assign cohort meal plan',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsAssigningGenericPlan(false);
    }
  }, [assignClientId, assignEndDate, assignGenericPlan, assignStartDate]);

  const exportMealPlanPdfById = React.useCallback(
    async (
      mealPlanId: string,
      options?: { planTitle?: string; planDate?: string; isGenericLibraryPlan?: boolean },
    ) => {
      const pdfOptions = {
        planTitle: options?.planTitle,
        planDate: options?.planDate,
        clinicName: user?.practiceName ?? undefined,
        clinicLogoUrl: user?.clinicLogoUrl ?? undefined,
      };

      if (options?.isGenericLibraryPlan) {
        await downloadGenericMealPlanPdfFromExportApi(mealPlanId, pdfOptions);
        return;
      }

      // Always fetch a fresh export payload so recently saved notes are included.
      // Stale `mealPlanExportData` (loaded when the board opened) omits later note edits.
      await downloadMealPlanPdfFromExportApi(mealPlanId, pdfOptions);
      void loadMealPlanExportData(mealPlanId);
    },
    [loadMealPlanExportData, user?.clinicLogoUrl, user?.practiceName],
  );

  const handleDownloadMealPlan = React.useCallback(
    async (meal: MealPlanListItem) => {
      if (!enableMealPlanPdfExport || !meal.id || downloadingMealPlanId === meal.id) return;

      setDownloadingMealPlanId(meal.id);
      try {
        await exportMealPlanPdfById(meal.id, {
          planTitle: meal.title?.trim() || undefined,
          isGenericLibraryPlan: isGenericLibraryMealPlan(meal),
        });
        showSuccessToast('Meal plan exported');
      } catch (error) {
        showErrorToast(error, 'Failed to download PDF');
      } finally {
        setDownloadingMealPlanId((current) => (current === meal.id ? null : current));
      }
    },
    [downloadingMealPlanId, enableMealPlanPdfExport, exportMealPlanPdfById],
  );

  const handleExportActiveMealPlanPdf = React.useCallback(async () => {
    if (!activeMealPlan?.id || isExportingActiveMealPlanPdf) {
      return;
    }

    setIsExportingActiveMealPlanPdf(true);
    try {
      const planCreatedAt =
        (activeMealPlanData as { createdAt?: string } | null)?.createdAt ??
        (activeMealPlanData?.searchParams as { createdAt?: string } | undefined)?.createdAt;

      await exportMealPlanPdfById(activeMealPlan.id, {
        planTitle: activeMealPlan.title?.trim() || undefined,
        planDate: planCreatedAt,
        isGenericLibraryPlan: isGenericLibraryMealPlan(activeMealPlan),
      });
      showSuccessToast('Meal plan exported');
    } catch (error) {
      showErrorToast(error, 'Failed to download PDF');
    } finally {
      setIsExportingActiveMealPlanPdf(false);
    }
  }, [
    activeMealPlan,
    activeMealPlanData,
    exportMealPlanPdfById,
    isExportingActiveMealPlanPdf,
  ]);

  const handleFinalizeActiveMealPlan = React.useCallback(async () => {
    if (!activeMealPlan?.id || isFinalizingActiveMealPlan) return;

    const draftId = activeMealPlan.id;
    const planName = activeMealPlan.title?.trim() || undefined;

    const runFinalize = async (overwriteConflicts: boolean) => {
      const response = await finalizeManualMealPlan(draftId, planName, undefined, overwriteConflicts);
      const finalized = response as { success?: boolean; data?: { mealPlanId?: string; id?: string } };
      if (!finalized.success) {
        throw new Error('Failed to finalize meal plan');
      }
      const planId = String(finalized.data?.mealPlanId ?? finalized.data?.id ?? draftId);

      // Reflect the finalized plan in place — keep the user on this screen, no
      // navigation/page reload. The board re-renders from the refetched data
      // (now non-draft), so the Finalise button drops away on its own.
      setActiveMealPlan((prev) =>
        prev ? { ...prev, id: planId, status: 'Finalized', rawStatus: 'finalized' } : prev,
      );
      const detailsResponse = await getMealPlanDetailsForView(planId, false);
      if (detailsResponse.success && detailsResponse.data) {
        setActiveMealPlanData(detailsResponse.data);
      }
      void loadMealPlanExportData(planId);
      setMealPlansRefreshKey((value) => value + 1);

      // Keep the URL's mealPlanId in sync (replace = no reload) so the list/detail
      // effects don't reopen the now-removed draft.
      if (planId !== draftId) {
        const nextParams = new URLSearchParams(location.search);
        nextParams.set('mealPlanId', planId);
        navigate({ pathname: location.pathname, search: `?${nextParams.toString()}` }, { replace: true });
      }

      showSuccessToast('Meal plan finalized');
    };

    setIsFinalizingActiveMealPlan(true);
    try {
      await runFinalize(false);
    } catch (error) {
      const conflicts = extractMealPlanDateConflicts(error);
      if (conflicts.length > 0) {
        const proceed = window.confirm(
          `${formatMealPlanDateConflictMessage(conflicts)}\n\nDo you want to overwrite the conflicting days and finalize this plan?`,
        );
        if (proceed) {
          try {
            await runFinalize(true);
            return;
          } catch (retryError) {
            showErrorToast(retryError, 'Error Finalizing Meal Plan');
            return;
          } finally {
            setIsFinalizingActiveMealPlan(false);
          }
        }
        setIsFinalizingActiveMealPlan(false);
        return;
      }
      showErrorToast(error, 'Error Finalizing Meal Plan');
    } finally {
      setIsFinalizingActiveMealPlan(false);
    }
  }, [
    activeMealPlan,
    isFinalizingActiveMealPlan,
    navigate,
    location.pathname,
    location.search,
    getMealPlanDetailsForView,
    loadMealPlanExportData,
  ]);

  return (
    <Box sx={{ mt: 2.25, minWidth: 0 }}>
      {!activeMealPlan ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: genericLibraryOnly ? 'flex-end' : 'space-between',
              gap: 1.25,
              flexDirection: { xs: 'column', md: 'row' },
              mb: 2.2,
            }}
          >
            {!showClientChronology && !genericLibraryOnly ? (
            <Tabs
              value={activeCategory}
              onChange={(_, value: MealPlanCategory) => setActiveCategory(value)}
              sx={{
                minHeight: 36,
                '& .MuiTabs-indicator': {
                  bgcolor: '#25a8da',
                  height: 2,
                  borderRadius: 999,
                },
                '& .MuiTab-root': {
                  minHeight: 36,
                  minWidth: 0,
                  px: 1.5,
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '0.86rem', md: '0.92rem' },
                  fontWeight: 500,
                  color: '#667085',
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#1498ca',
                },
              }}
            >
              {categoryTabs.map((tab) => {
                const count = totalByCategory[tab.key];
                const isActive = activeCategory === tab.key;

                return (
                  <Tab
                    key={tab.key}
                    value={tab.key}
                    label={
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                        <Box component="span">{tab.label}</Box>
                        <Box
                          component="span"
                          sx={{
                            minWidth: 18,
                            px: 0.45,
                            py: 0.05,
                            borderRadius: 99,
                            bgcolor: isActive ? '#25a8da' : '#eef2f6',
                            color: isActive ? '#fff' : '#667085',
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                            textAlign: 'center',
                          }}
                        >
                          {count}
                        </Box>
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
            ) : !genericLibraryOnly ? (
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.94rem', fontWeight: 700, color: '#344054' }}>
                Meal plan timeline
              </Typography>
            ) : null}

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                width: { xs: '100%', md: 'auto' },
                ml: genericLibraryOnly ? 'auto' : 0,
                justifyContent: genericLibraryOnly ? 'flex-end' : 'flex-start',
              }}
            >
              {genericLibraryOnly ? (
                <SlashSearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onClear={() => setSearchQuery('')}
                  placeholder="Search"
                  sx={{
                    width: { xs: '100%', md: 240 },
                    flex: { xs: 1, md: '0 0 auto' },
                    '& .MuiOutlinedInput-root': {
                      height: 38,
                      borderRadius: '999px',
                      bgcolor: '#fff',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.82rem',
                      color: '#667085',
                      '& fieldset': {
                        borderColor: '#d0d5dd',
                      },
                      '&:hover fieldset': {
                        borderColor: '#b8c2cc',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#20AEDC',
                        borderWidth: '1px',
                      },
                    },
                  }}
                />
              ) : (
                <TextField
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search"
                  size="small"
                  sx={{
                    minWidth: { xs: 0, md: 240 },
                    flex: { xs: 1, md: '0 0 auto' },
                    '& .MuiOutlinedInput-root': {
                      height: 38,
                      borderRadius: '999px',
                      bgcolor: '#fff',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.82rem',
                      color: '#667085',
                      '& fieldset': {
                        borderColor: '#d0d5dd',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: '#98a2b3', fontSize: 17 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box
                          component="span"
                          sx={{
                            minWidth: 20,
                            px: 0.4,
                            py: 0.05,
                            borderRadius: 999,
                            border: '1px solid #eaecf0',
                            color: '#98a2b3',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                            textAlign: 'center',
                            lineHeight: 1.4,
                          }}
                        >
                          /
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <Button
                data-demo-id="meal-plan-add"
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  flexShrink: 0,
                  height: 38,
                  px: 1.8,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.92rem',
                  fontWeight: 600,
                  bgcolor: '#25a8da',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#1498ca',
                    boxShadow: 'none',
                  },
                }}
                onClick={() => {
                  const nextPlanType = isMealPlanningRoute && activeCategory === 'generic' ? 'manual' : activeCategory;
                  trackAddMealPlanClicked({
                    ...getMealPlanEntryProperties(),
                    selected_meal_plan_type: nextPlanType,
                    show_client_selection: showClientSelection,
                  });
                  setSelectedPlanType(nextPlanType);
                  setNewMealName('');
                  setAddMealPlanFieldErrors({});
                  setAddMealPlanFormFeedback(null);
                  if (nextPlanType === 'generic') {
                    setSelectedGenericClinicalTags([]);
                    setGenericCalorieTarget(DEFAULT_GENERIC_CALORIE_TARGET);
                  }
                  if (showClientSelection) {
                    setSelectedClientQuery('');
                    setSelectedClientId('');
                    setIsClientDropdownOpen(false);
                  }
                  setIsAddMealModalOpen(true);
                }}
                data-analytics-ignore="true"
              >
                {genericLibraryOnly ? 'Create Cohort Meal Plan' : 'Add Meal Plan'}
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box
              sx={{
                mt: 2.5,
                border: '1px solid #dbe6eb',
                borderRadius: '12px',
                py: 4,
                display: 'grid',
                placeItems: 'center',
                bgcolor: '#fff',
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box
              sx={{
                mt: 2.5,
                display: showClientChronology ? 'grid' : 'block',
                gridTemplateColumns: { xs: '1fr', lg: showClientChronology ? 'minmax(0, 1fr) 220px' : '1fr' },
                alignItems: 'start',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'grid', gap: 1.2, minWidth: 0 }}>
                {mealPlansLoadError ? (
                  <Box
                    sx={{
                      border: '1px solid #f4cccc',
                      borderRadius: '12px',
                      px: 2,
                      py: 2.5,
                      bgcolor: '#fff4f4',
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: '#9b1c1c' }}>
                      {mealPlansLoadError}
                    </Typography>
                  </Box>
                ) : listMealPlans.length ? (
                  showClientChronology ? (
                    ([
                      { key: 'future' as const, label: 'Future meal plans', items: timelineGroups.future, collapsible: true },
                      { key: 'current' as const, label: 'Current active meal plan', items: timelineGroups.current, collapsible: false },
                      { key: 'past' as const, label: 'Past meal plans', items: timelineGroups.past, collapsible: true },
                    ]).map((section) => {
                      if (!section.items.length) return null;
                      const isExpanded = section.collapsible ? timelineExpanded[section.key] : true;
                      const visibleItems = section.collapsible && !isExpanded ? section.items.slice(0, 1) : section.items;
                      return (
                        <Box key={section.key} sx={{ display: 'grid', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: section.key === 'future' ? 0 : 1.2 }}>
                            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.86rem', fontWeight: 700, color: section.key === 'current' ? '#137333' : '#475467' }}>
                              {section.label}
                            </Typography>
                            {section.collapsible && section.items.length > 1 ? (
                              <Button
                                size="small"
                                onClick={() => setTimelineExpanded((prev) => ({ ...prev, [section.key]: !prev[section.key] }))}
                                sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif', color: '#1498ca', fontWeight: 600 }}
                              >
                                {isExpanded ? 'Collapse' : `Expand ${section.items.length}`}
                              </Button>
                            ) : null}
                          </Box>
                          {visibleItems.map((meal, index) => (
                            <ClientDetailsV2MealPlanListItem
                              key={meal.id}
                              meal={meal}
                              index={index}
                              highlightCurrent={section.key === 'current'}
                              hideStatusChip={false}
                              hideSubtitle
                              onView={() => handleViewMealPlan(meal)}
                              onDownload={
                                enableMealPlanPdfExport && meal.rawStatus !== 'draft'
                                  ? () => {
                                      void handleDownloadMealPlan(meal);
                                    }
                                  : undefined
                              }
                              downloading={downloadingMealPlanId === meal.id}
                              onDelete={() => {
                                void handleDeleteMealPlan(meal);
                              }}
                              deleting={deletingMealPlanId === meal.id}
                            />
                          ))}
                        </Box>
                      );
                    })
                  ) : (
                    listMealPlans.map((meal, index) => (
                      <ClientDetailsV2MealPlanListItem
                        key={meal.id}
                        meal={meal}
                        index={index}
                        hideStatusChip={activeCategory === 'generic'}
                        hideSubtitle={location.pathname !== '/meal-planning' && !genericLibraryOnly}
                        onView={() => handleViewMealPlan(meal)}
                        onDownload={
                          enableMealPlanPdfExport && meal.rawStatus !== 'draft'
                            ? () => {
                                void handleDownloadMealPlan(meal);
                              }
                            : undefined
                        }
                        onAssign={genericLibraryOnly && isGenericLibraryMealPlan(meal) ? () => openAssignGenericDialog(meal) : undefined}
                        downloading={downloadingMealPlanId === meal.id}
                        onDelete={() => {
                          void handleDeleteMealPlan(meal);
                        }}
                        deleting={deletingMealPlanId === meal.id}
                      />
                    ))
                  )
                ) : (
                  <Box
                    sx={{
                      border: '1px dashed #d0d5dd',
                      borderRadius: '12px',
                      px: 2,
                      py: 3,
                      bgcolor: '#fff',
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: '#667085' }}>
                      No meal plans.
                    </Typography>
                  </Box>
                )}
                {usePaginatedMealPlanList && listMealPlans.length > 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                    <PaginationControls
                      currentPage={mealPlanListPage}
                      totalPages={mealPlanListPagination.totalPages}
                      onPageChange={setMealPlanListPage}
                      totalCount={mealPlanListPagination.totalCount}
                      pageSize={MEAL_PLAN_LIST_PAGE_SIZE}
                      isLoading={loading}
                      showSummary
                    />
                  </Box>
                ) : null}
              </Box>

              {showClientChronology ? (
                <Box
                  sx={{
                    border: '1px solid #dbe6eb',
                    borderRadius: '12px',
                    bgcolor: '#fff',
                    p: 1,
                    position: { lg: 'sticky' },
                    top: { lg: 16 },
                  }}
                >
                  <Tabs
                    value={clientTimelineCategory}
                    orientation="vertical"
                    onChange={(_, value: ClientTimelineCategory) => {
                      setClientTimelineCategory(value);
                      if (value === 'manual' || value === 'generic' || value === 'ai') setActiveCategory(value);
                    }}
                    sx={{
                      minHeight: 0,
                      '& .MuiTabs-indicator': {
                        display: 'none',
                      },
                      '& .MuiTab-root': {
                        minHeight: 38,
                        alignItems: 'stretch',
                        borderRadius: '9px',
                        px: 1.25,
                        py: 0.75,
                        mb: 0.35,
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.88rem',
                        fontWeight: 600,
                        color: '#667085',
                      },
                      '& .MuiTab-root.Mui-selected': {
                        bgcolor: '#e8f6fb',
                        color: '#1498ca',
                      },
                    }}
                  >
                    {clientTimelineTabs.map((tab) => {
                      const count = clientTimelineCounts[tab.key];
                      const isActive = clientTimelineCategory === tab.key;
                      return (
                        <Tab
                          key={tab.key}
                          value={tab.key}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                              <Box component="span">{tab.label}</Box>
                              <Box
                                component="span"
                                sx={{
                                  minWidth: 18,
                                  px: 0.45,
                                  py: 0.05,
                                  borderRadius: 99,
                                  bgcolor: isActive ? '#25a8da' : '#eef2f6',
                                  color: isActive ? '#fff' : '#667085',
                                  fontSize: '0.75rem',
                                  lineHeight: 1.4,
                                  textAlign: 'center',
                                }}
                              >
                                {count}
                              </Box>
                            </Box>
                          }
                        />
                      );
                    })}
                  </Tabs>
                </Box>
              ) : null}
            </Box>
          )}
        </>
      ) : isLoadingMealPlanDetails ? (
        <Box
          sx={{
            mt: 2.5,
            border: '1px solid #dbe6eb',
            borderRadius: '12px',
            py: 6,
            display: 'grid',
            placeItems: 'center',
            bgcolor: '#fff',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : activeMealPlanData ? (
        <>
          <ClientDetailsV2MealPlanBoardView
            planName={activeMealPlan.title}
            planDurationLabel={activeMealPlan.durationLabel}
            data={activeMealPlanData}
            client={client}
            onBack={handleCloseMealPlanView}
            onEditDay={handleEditMealDay}
            onAddDay={handleAddDay}
            onCopyDay={handleCopyDay}
            isCopyingDay={isCopyingDay}
            onSaveDayName={handleSaveMealDayName}
            isSavingDayName={isSavingDayName}
            onSaveMealPlanNotes={handleSaveMealPlanNotes}
            isSavingMealPlanNotes={isSavingMealPlanNotes}
            onDeleteDay={handleDeleteMealDay}
            isDeletingDay={isDeletingDay}
            onReorderDays={handleReorderMealDays}
            isReorderingDays={isReorderingDays}
            onReorderMealsInDay={handleReorderMealsInDay}
            isReorderingMeals={isReorderingMeals}
            onMealPlanUpdated={refreshActiveMealPlanData}
            planMode={isGenericLibraryMealPlan(activeMealPlan) ? 'generic' : 'manual'}
            planCategory={activeMealPlan.category}
            hideGenericToolbarAddMeal={isGenericLibraryMealPlan(activeMealPlan)}
            onExportPdf={enableMealPlanPdfExport ? handleExportActiveMealPlanPdf : undefined}
            isExportingPdf={isExportingActiveMealPlanPdf}
            onFinalize={handleFinalizeActiveMealPlan}
            isFinalizing={isFinalizingActiveMealPlan}
          />
          {activeMealPlanData.status !== 'draft' && !isGenericLibraryMealPlan(activeMealPlan) ? (
            <MealPlanClientFeedbackPanel
              mealPlanId={activeMealPlan.id}
              mealPlanName={activeMealPlan.title}
            />
          ) : null}
        </>
      ) : (
        <Box
          sx={{
            mt: 2.5,
            border: '1px solid #dbe6eb',
            borderRadius: '12px',
            px: 2,
            py: 3,
            bgcolor: '#fff',
            display: 'grid',
            gap: 1.2,
            justifyItems: 'start',
          }}
        >
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: '#667085' }}>
            {mealPlanDetailsError || 'Unable to load this meal plan.'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCloseMealPlanView}
            sx={{
              textTransform: 'none',
              borderColor: '#d0d5dd',
              color: '#475467',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
            }}
          >
            Back To Meal Plans
          </Button>
        </Box>
      )}

      <ClientDetailsV2AddMealPlanDialog
        open={isAddMealModalOpen}
        onClose={() => setIsAddMealModalOpen(false)}
        showClientSelection={showClientSelection}
        selectedClientQuery={selectedClientQuery}
        onClientSearchChange={(value) => {
          setSelectedClientQuery(value);
          setSelectedClientId('');
          setIsClientDropdownOpen(true);
          setAddMealPlanFieldErrors((current) => ({ ...current, client: undefined }));
          setAddMealPlanFormFeedback(null);
        }}
        isClientDropdownOpen={isClientDropdownOpen}
        onClientDropdownOpenChange={setIsClientDropdownOpen}
        clientOptions={clientOptions}
        isClientsLoading={isClientsLoading}
        getClientDisplayName={getClientDisplayName}
        getClientConditionSummary={getClientConditionSummary}
        onPickClient={(client) => {
          setSelectedClientId(client.id);
          setSelectedClientQuery(getClientDisplayName(client));
          setIsClientDropdownOpen(false);
          setAddMealPlanFieldErrors((current) => ({ ...current, client: undefined }));
          setAddMealPlanFormFeedback(null);
        }}
        newMealName={newMealName}
        onNewMealNameChange={(value) => {
          setNewMealName(value);
          setAddMealPlanFieldErrors((current) => ({ ...current, mealName: undefined }));
          setAddMealPlanFormFeedback(null);
        }}
        newMealDays={newMealDays}
        onNewMealDaysChange={setNewMealDays}
        genericClinicalTagOptions={genericClinicalTags}
        selectedGenericClinicalTags={selectedGenericClinicalTags}
        onGenericClinicalTagsChange={(value) => {
          setSelectedGenericClinicalTags(value);
          setAddMealPlanFieldErrors((current) => ({ ...current, clinicalTags: undefined }));
          setAddMealPlanFormFeedback(null);
        }}
        onGenericClinicalTagsOpen={loadGenericClinicalTags}
        isGenericClinicalTagsLoading={isGenericClinicalTagsLoading}
        genericCalorieTarget={selectedPlanType === 'generic' ? genericCalorieTarget : undefined}
        onGenericCalorieTargetChange={(value) => {
          setGenericCalorieTarget(value);
          setAddMealPlanFieldErrors((current) => ({
            ...current,
            calorieTarget: undefined,
            ageYears: undefined,
            gender: undefined,
            weightKg: undefined,
            heightCm: undefined,
            activityLevel: undefined,
          }));
          setAddMealPlanFormFeedback(null);
        }}
        selectedPlanType={selectedPlanType}
        onSelectedPlanTypeChange={(value) => {
          trackMealPlanTypeSelected({
            ...getMealPlanEntryProperties(),
            selected_meal_plan_type: value,
          });
          setSelectedPlanType(value);
          if (value === 'ai' || value === 'auto') {
            setNewMealDays((current) => Math.min(current, AI_MEAL_PLAN_MAX_DAYS));
          }
          if (value === 'generic') {
            setNewMealName('');
            setSelectedGenericClinicalTags([]);
            setGenericCalorieTarget(DEFAULT_GENERIC_CALORIE_TARGET);
          }
          setAddMealPlanFieldErrors({});
          setAddMealPlanFormFeedback(null);
        }}
        onSubmit={handleSubmitNewMealPlan}
        isSubmitting={isCreatingGenericMealPlan}
        showDaysField={!genericLibraryOnly}
        showPlanTypeOptions={!genericLibraryOnly}
        showGenericPlanTypeOption={!showClientChronology && !isMealPlanningRoute}
        paperMaxWidth={showClientChronology || isMealPlanningRoute ? 640 : undefined}
        fieldErrors={addMealPlanFieldErrors}
        formFeedback={addMealPlanFormFeedback}
      />

      <Dialog
        open={Boolean(assignGenericPlan)}
        onClose={closeAssignGenericDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { overflow: 'visible' },
        }}
      >
        <DialogTitle>Assign cohort meal plan</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 1, overflow: 'visible' }}>
          <Typography sx={{ fontSize: '0.92rem', color: '#475467' }}>
            {assignGenericPlan?.title || 'Cohort meal plan'}
          </Typography>
          <Box>
            <SearchableClientDropdown
              value={assignClientId || undefined}
              onChange={(nextClientId) => {
                setAssignClientId(nextClientId || '');
                setAssignDateConflicts([]);
                setAssignGenericFieldErrors((current) => ({ ...current, client: undefined }));
                setAssignGenericFormFeedback(null);
              }}
              placeholder="Search and select client..."
              disabled={isAssigningGenericPlan}
              displayVariant="questionnaire"
            />
            {assignGenericFieldErrors.client ? (
              <Typography sx={{ mt: 0.6, fontSize: '0.78rem', color: '#dc2626' }}>
                {assignGenericFieldErrors.client}
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={assignStartDate}
              onChange={(event) => handleAssignStartDateChange(event.target.value)}
              disabled={isAssigningGenericPlan}
              error={Boolean(assignGenericFieldErrors.startDate)}
              helperText={assignGenericFieldErrors.startDate}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: assignDateToday }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={assignEndDate}
              onChange={(event) => handleAssignEndDateChange(event.target.value)}
              disabled={isAssigningGenericPlan}
              error={Boolean(assignGenericFieldErrors.endDate)}
              helperText={assignGenericFieldErrors.endDate || `Maximum ${MEAL_PLAN_MAX_DAYS} days`}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: assignStartDate || assignDateToday,
                max: assignMaxEndDate,
              }}
            />
          </Box>
          {assignGenericFormFeedback ? (
            <InlineFeedback
              variant={assignGenericFormFeedback.variant}
              title={assignGenericFormFeedback.title}
              message={assignGenericFormFeedback.message}
            />
          ) : null}
          {assignDateConflicts.length > 0 ? (
            <Box sx={{ border: '1px solid #fbbf24', borderRadius: '10px', bgcolor: '#fffbeb', p: 1.5, textAlign: 'left' }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#92400e' }}>
                These active plans will be marked inactive:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 0, color: '#92400e', listStyle: 'none' }}>
                {assignDateConflicts.map((item) => (
                  <li key={item.id}>
                    <Typography component="span" sx={{ display: 'block', fontSize: '0.84rem', textAlign: 'left' }}>
                      {item.name} ({formatDateRangeLabel(item.startDate || undefined, item.endDate || undefined)})
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAssignGenericDialog} disabled={isAssigningGenericPlan} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              void submitAssignGenericPlan(assignDateConflicts.length > 0);
            }}
            disabled={isAssigningGenericPlan}
            variant="contained"
            sx={{ bgcolor: '#137333', '&:hover': { bgcolor: '#0f6b2f' } }}
          >
            {isAssigningGenericPlan ? 'Assigning...' : assignDateConflicts.length > 0 ? 'Overwrite And Assign' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirmDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete meal plan?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.95rem', color: '#475467' }}>
            {(() => {
              const mealName = pendingDeleteMealPlan?.title?.trim() || 'this meal plan';
              if (isGenericLibraryMealPlan(pendingDeleteMealPlan)) {
                return `This will permanently delete "${mealName}". This action cannot be undone.`;
              }
              if (pendingDeleteMealPlan?.rawStatus === 'draft') {
                return `This will delete "${mealName}" and mark it as expired.`;
              }
              return `This will archive "${mealName}". You can restore it later if needed.`;
            })()}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteConfirmDialog} disabled={Boolean(deletingMealPlanId)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleDeleteMealPlanConfirm();
            }}
            disabled={Boolean(deletingMealPlanId)}
            variant="contained"
            sx={{
              bgcolor: '#FF6262',
              '&:hover': { bgcolor: '#FF6262' },
            }}
          >
            {deletingMealPlanId ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
